import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { kebabHtml, bindKebabs, closeKebab, type KebabItem } from '../shared/components/Kebab';
import { MtrTenantsTab } from './mtrTenants/MtrTenantsTab';
import { LeadsInbox } from './LeadsInbox';
import { LeadsAgentPanel } from './LeadsAgentPanel';
// R116 #7: the Pipeline's tour UI (Schedule-tour sheet, drawer Tours section, card chip, home change)
import { PipelineTours, TOURS_CSS } from './mtr/tours';
// R122: MTR → Holds — the MTR Hold Agent's plans, preview / confirm, actions, mode, digest (pages/mtrHolds)
import { MtrHoldsPanel } from './mtrHolds/MtrHoldsPanel';

// MTR Operations — mid-term rental ops in one place (API: /api/mtr).
//
// The product (owner's words): "we always know when an MTR property is coming
// vacant; we know where all the leads are; we have MTR rates for all
// properties; tenants verified; payments on time; we're reaching out to
// contacts; we're listed on the right channels."
//
// Tabs: LEAD INBOX (every Zillow / Furnished Finder / Avail inquiry in one
// list, answered here — LeadsInbox.ts), VACANCY RADAR (the queue, sorted by
// urgency), RATES (12-month grid, AirROI-suggested vs manual), PIPELINE
// (kanban with verification checks + payment tracking), TENANTS, CONTACTS
// (outreach drafts — copy only, never sent), LEADS AGENT (the always-on Mac
// that syncs the platforms + its live browser — LeadsAgentPanel.ts).
// PRINCIPLES.md: every number states its basis inline (as-of date, source,
// thresholds), and gaps that can't be computed honestly say why.
// Fallback copy only — the board uses the `stages` list GET /api/mtr/leads
// sends (lib/mtrLeads.js STAGES is the single source).
const STAGES = ['new', 'contacted', 'qualified', 'touring', 'applied', 'screening', 'lease_sent', 'signed', 'active', 'lost', 'ended'];
// R114 #3: any stage → any stage. Only two kinds of move ask first (nothing is
// ever deleted by a move — it guards against a mis-drag): moving BACK out of
// Signed / Active, and any move out of Lost / Ended (a reopen). Moving INTO
// Lost / Ended offers an optional reason, saved as a note in the same write.
const TERMINAL = ['lost', 'ended'];
const SETTLED = ['signed', 'active'];
const CHECKS: Array<{ key: string; short: string; label: string }> = [
  { key: 'background', short: 'B', label: 'Background check' },
  { key: 'credit', short: 'C', label: 'Credit check' },
  { key: 'lease', short: 'L', label: 'Lease signed' },
  { key: 'deposit', short: 'D', label: 'Deposit received' },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// R114: the Pipeline card drawer + the move toast. Rendered once per render()
// OUTSIDE #mtr-body (draw() rewrites #mtr-body; the drawer must survive it).
// HostBuddy idiom: :root tokens only, --accent is the one action colour.
const DRAWER_CSS = `
  .mtr-dr-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:9500; display:flex; justify-content:flex-end; }
  .mtr-dr-overlay.enter { animation:mtrDrFade .14s ease-out; }
  .mtr-dr-overlay.enter .mtr-dr { animation:mtrDrIn .18s ease-out; }
  @keyframes mtrDrFade { from { background:rgba(15,23,42,0); } }
  @keyframes mtrDrIn { from { transform:translateX(24px); opacity:.6; } }
  .mtr-dr { width:min(520px, 100vw); height:100%; background:var(--card); box-shadow:var(--shadow-pop); display:flex; flex-direction:column; border-radius:var(--radius) 0 0 var(--radius); overflow:hidden; }
  .mtr-dr-head { padding:16px 20px 14px; border-bottom:1px solid var(--border); background:var(--card); }
  .mtr-dr-eyebrow { display:flex; align-items:center; gap:10px; font-size:11.5px; color:var(--text-muted); min-width:0; }
  .mtr-dr-tag { flex:none; font-size:10px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:var(--radius-pill); padding:2px 8px; }
  .mtr-dr-meta { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .mtr-dr-x { flex:none; margin-left:auto; border:none; background:none; font-size:16px; color:var(--text-muted); cursor:pointer; min-width:36px; height:36px; border-radius:var(--radius-sm); line-height:1; }
  .mtr-dr-x:hover { background:var(--border-soft); color:var(--text); }
  .mtr-dr-name { margin:4px 0 0; font-size:19px; font-weight:700; letter-spacing:-.01em; color:var(--text); overflow-wrap:anywhere; }
  .mtr-dr-stage { display:flex; align-items:center; gap:10px; margin-top:12px; flex-wrap:wrap; }
  .mtr-dr-stage label { display:flex; align-items:center; gap:8px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); }
  .mtr-dr-stage select { font:inherit; font-size:13px; font-weight:600; text-transform:none; letter-spacing:0; color:var(--text); padding:7px 10px; min-height:36px; border:1px solid var(--border); border-radius:var(--radius-btn); background:var(--card); cursor:pointer; }
  .mtr-dr-stage select:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
  .mtr-dr-stage .btn { min-height:36px; }
  .mtr-dr-body { flex:1; overflow-y:auto; padding:16px 20px 28px; background:var(--bg); display:flex; flex-direction:column; gap:14px; overscroll-behavior:contain; }
  .mtr-dr-sec { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; }
  .mtr-dr-sec h4 { margin:0 0 10px; display:flex; align-items:center; gap:8px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-faint); }
  .mtr-dr-sec .cnt, .mtr-dr-hist .cnt { font-size:10.5px; font-weight:700; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-pill); padding:0 7px; letter-spacing:0; font-variant-numeric:tabular-nums; }
  .mtr-dr-ta { display:block; width:100%; box-sizing:border-box; min-height:76px; resize:vertical; font:inherit; font-size:13px; line-height:1.5; padding:9px 11px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text); }
  .mtr-dr-ta:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
  .mtr-dr-row { display:flex; align-items:center; gap:8px; margin-top:8px; flex-wrap:wrap; }
  .mtr-dr-row .btn { min-height:34px; }
  .mtr-dr-hint { flex:1; min-width:140px; font-size:11px; color:var(--text-faint); }
  .mtr-dr-notes { display:flex; flex-direction:column; gap:8px; margin-top:14px; }
  .mtr-dr-note { background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:9px 11px; }
  .mtr-dr-note.editing { background:var(--card); border-color:var(--accent-border); }
  .mtr-dr-note-h { display:flex; align-items:center; gap:5px; font-size:11px; color:var(--text-muted); min-height:24px; }
  .mtr-dr-note-h .who { font-weight:600; color:var(--text); }
  .mtr-dr-note-h .who.anon { font-weight:500; font-style:italic; color:var(--text-faint); }
  .mtr-dr-note-h .kb-btn { margin-left:auto; color:var(--text-muted); }
  .mtr-dr-note-t { margin-top:3px; font-size:12.5px; line-height:1.5; color:var(--text); white-space:pre-wrap; overflow-wrap:anywhere; }
  .mtr-dr-note .mtr-dr-ta { margin-top:6px; }
  .mtr-dr-empty { font-size:12px; color:var(--text-faint); padding:4px 0; }
  .mtr-dr-kv { display:grid; grid-template-columns:84px minmax(0,1fr); gap:10px; padding:7px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; }
  .mtr-dr-kv:last-child { border-bottom:none; }
  .mtr-dr-kv .k { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); padding-top:2px; }
  .mtr-dr-kv .v { min-width:0; color:var(--text); overflow-wrap:anywhere; }
  .mtr-dr-kv a, .mtr-dr-check a { color:var(--accent); font-weight:600; text-decoration:none; }
  .mtr-dr-kv a:hover, .mtr-dr-check a:hover { text-decoration:underline; }
  .mtr-dr-check { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid var(--border-soft); }
  .mtr-dr-check:last-child, .mtr-dr-pay:last-child { border-bottom:none; }
  .mtr-dr-check .mtr-check { width:32px; height:32px; flex:none; font-size:11px; border-radius:9px; }
  .mtr-dr-check-b { min-width:0; flex:1; font-size:12.5px; }
  .mtr-dr-check-b .t { font-weight:600; color:var(--text); display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .mtr-dr-check-b .st { font-size:10px; font-weight:600; padding:2px 8px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); }
  .mtr-dr-check-b .st.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
  .mtr-dr-check-b .n { margin-top:3px; color:var(--text-muted); white-space:pre-wrap; overflow-wrap:anywhere; }
  .mtr-dr-pay { display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; flex-wrap:wrap; }
  .mtr-dr-pay .n { color:var(--text-muted); overflow-wrap:anywhere; }
  .mtr-dr-paybtn { font:inherit; font-size:11px; font-weight:600; border-radius:var(--radius-pill); padding:4px 12px; min-height:30px; cursor:pointer; border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); }
  .mtr-dr-paybtn.paid { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
  .mtr-dr-paybtn.late { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); }
  .mtr-dr-hist summary { cursor:pointer; display:flex; align-items:center; gap:8px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-faint); min-height:24px; list-style:none; }
  .mtr-dr-hist summary::-webkit-details-marker { display:none; }
  .mtr-dr-hist summary::before { content:'▸'; font-size:11px; color:var(--text-muted); transition:transform .12s ease; }
  .mtr-dr-hist[open] summary::before { transform:rotate(90deg); }
  .mtr-dr-hist summary:hover { color:var(--text-muted); }
  .mtr-dr-hist ul { list-style:none; margin:10px 0 0; padding:0; display:flex; flex-direction:column; gap:8px; }
  .mtr-dr-hist li { display:flex; flex-direction:column; gap:1px; font-size:12px; line-height:1.4; overflow-wrap:anywhere; padding-bottom:7px; border-bottom:1px solid var(--border-soft); }
  .mtr-dr-hist li:last-child { border-bottom:none; padding-bottom:0; }
  .mtr-dr-hist .what { color:var(--text); }
  .mtr-dr-hist .meta { font-size:11px; color:var(--text-faint); }
  /* the shared ⋯ popover (z 340) must sit above this drawer (z 9500) — the per-note menus open inside it */
  body .kb-pop[data-kebab-pop] { z-index:9550; }
  .mtr-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%); z-index:9600; display:flex; align-items:center; gap:14px; max-width:calc(100vw - 32px); box-sizing:border-box; padding:10px 12px 10px 16px; border-radius:var(--radius-btn); background:var(--text); color:var(--card); box-shadow:var(--shadow-pop); font-size:12.5px; }
  .mtr-toast button { font:inherit; font-weight:700; color:var(--accent-border); background:none; border:none; cursor:pointer; padding:6px 8px; min-height:32px; border-radius:var(--radius-sm); }
  .mtr-toast button:hover { background:rgba(255,255,255,.08); }
  @media (max-width: 640px) {
    .mtr-dr { width:100vw; border-radius:0; }
    .mtr-dr-head { padding:12px 16px 12px; }
    .mtr-dr-body { padding:12px 12px 24px; gap:12px; }
    .mtr-dr-sec { padding:12px 14px; }
    .mtr-dr .kb-btn { min-width:36px; height:36px; }
    .mtr-dr-check .mtr-check { width:36px; height:36px; }
    .mtr-dr-paybtn { min-height:36px; }
    .mtr-dr-row .btn { min-height:36px; }
    .mtr-dr-kv { grid-template-columns:72px minmax(0,1fr); }
    .mtr-toast { bottom:16px; }
  }
`;
type MtrTab = 'inbox' | 'vacancy' | 'rates' | 'leads' | 'tenants' | 'holds' | 'contacts' | 'agent';
// 'leads' is the pipeline kanban (id kept for back-compat); inbox first, agent last.
const TABS: Array<[MtrTab, string]> = [
  ['inbox', 'Lead inbox'], ['vacancy', 'Vacancy radar'], ['rates', 'Rates'], ['leads', 'Pipeline'],
  ['tenants', 'Tenants'], ['holds', 'Holds'], ['contacts', 'Contacts'], ['agent', 'Leads agent'],
];

export class MtrPage {
  private container: HTMLElement;
  private tab: MtrTab = 'inbox';
  private vacancy: any = null;
  private rates: any = null;
  private standardRates: any = null;
  private leadsData: any = null;
  private tenantsData: any = null;
  private contactsData: any = null;
  private priceResults: Record<string, any> = {};
  // The inbox + agent panel are kept alive across redraws (they hold the open
  // lead, drafts, unsaved settings and the live viewer) and re-mounted into
  // the fresh #mtr-tab-body on every draw().
  private inbox = new LeadsInbox({
    openAgentTab: () => { this.tab = 'agent'; this.draw(); },
    // R114: "View pipeline →" opens that renter's card drawer
    openPipelineTab: (mtrLeadId?: string) => {
      this.tab = 'leads'; this.draw();
      if (mtrLeadId && mtrLeadId !== 'yes') void this.openDrawerById(mtrLeadId);
    },
    onPipelineAdded: () => { void this.reloadLeads(); },
  });
  private agentPanel = new LeadsAgentPanel();
  private holdsPanel = new MtrHoldsPanel();   // R122: kept alive across redraws, re-mounted into #mtr-tab-body
  // ── R114 pipeline board state (the drawer lives OUTSIDE #mtr-body, so a
  // board redraw never wipes an open drawer or a half-typed note) ──────────
  private drawerLeadId: string | null = null;
  private focusComposer = false;
  private noteDraft = '';
  private noteBusy = false;
  private editNote: { id: string; text: string; orig: string; busy?: boolean } | null = null;
  private histOpen = false;
  private dragLeadId: string | null = null;
  private moving = new Set<string>();
  private people: Array<{ email: string; full_name: string }> | null = null;
  private peopleLoading = false;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private escOff: (() => void) | null = null;
  // R116 #4 (Kai): the Tenants tab — cards, notes + follow-ups, the Edit sheet (pages/mtrTenants/MtrTenantsTab.ts)
  private tenantsTab = new MtrTenantsTab({
    data: () => this.tenantsData,
    applyTenant: (v: any) => this.applyTenant(v),
    removeTenant: (id: string) => { if (this.tenantsData && Array.isArray(this.tenantsData.tenants)) this.tenantsData.tenants = this.tenantsData.tenants.filter((t: any) => t && t.id !== id); },
    reload: () => this.reloadTenants(),
    redraw: () => { if (this.tab === 'tenants') this.draw(); },
    whoName: (by: string) => this.whoName(by),
    loadPeople: () => this.loadPeople(),
    toast: (text: string) => this.showToast(this.esc(text)),
  });
  private tours: PipelineTours;

  constructor(container: HTMLElement) {
    this.container = container;
    // R116 #7: the tour UI talks to the page through this narrow host
    this.tours = new PipelineTours({
      container,
      lead: (id) => this.leadById(id),
      properties: () => ((this.leadsData?.all_properties?.length ? this.leadsData.all_properties : (this.vacancy?.properties || [])) as any[]).map((p: any) => ({ listing_id: String(p.listing_id), name: String(p.name || p.listing_id) })),
      listingName: (id) => this.listingName(id),
      people: () => this.people || [],
      loadPeople: () => this.loadPeople(),
      leads: () => (this.leadsData?.leads || []),
      send: (m, p, b) => this.send(m as any, p, b),
      applyLead: (v) => { if (v && v.id) this.replaceLead(v); this.draw(); this.patchDrawer(); },
      reloadLeads: async () => { await this.reloadLeads(); this.draw(); this.patchDrawer(); },
      toast: (html) => this.showToast(html),
      justMove: (id, from) => { void this.moveLead(id, 'touring', { skipTour: true, confirmed: true, from }); },
    });
  }

  // App's 15-min auto-refresh veto — only the live tabs have state to protect.
  // R114: the Pipeline tab too, while a card drawer is open, a note is being
  // typed / edited, a card is mid-drag, or a move is still saving.
  suspendAutoRefresh(): boolean {
    if (this.tab === 'inbox') return this.inbox.suspendAutoRefresh();
    if (this.tab === 'agent') return this.agentPanel.suspendAutoRefresh();
    if (this.tab === 'leads') return !!(this.drawerLeadId || this.noteDraft.trim() || this.editNote || this.dragLeadId || this.moving.size || this.tours.isOpen());
    if (this.tab === 'tenants') return this.tenantsTab.busy();   // R116: an Edit sheet open / a typed tenant note
    if (this.tab === 'holds') return this.holdsPanel.suspendAutoRefresh();   // R122: a hold form open / saving
    return false;
  }

  async render(): Promise<void> {
    // one-shot deep link: sessionStorage.setItem('mtr_tab', 'agent') then navigate to 'mtr'
    // R115: + 'mtr_open_lead' = <lead id> (the Calendar's "Open card in Pipeline")
    // opens that card's drawer once the board has loaded.
    let openLead: string | null = null;
    try {
      const want = sessionStorage.getItem('mtr_tab');
      if (want) {
        sessionStorage.removeItem('mtr_tab');
        if (TABS.some(([k]) => k === want)) this.tab = want as MtrTab;
      }
      const lid = sessionStorage.getItem('mtr_open_lead');
      if (lid) {
        sessionStorage.removeItem('mtr_open_lead');
        if (/^[\w-]{1,80}$/.test(lid)) { openLead = lid; this.tab = 'leads'; }
      }
    } catch { /* private mode */ }
    this.container.innerHTML = `
      <div class="page-content">
        <style>${DRAWER_CSS}${TOURS_CSS}</style>
        <div id="mtr-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
        <div id="mtr-drawer-host"></div>
        <div id="mtr-toast-host"></div>
      </div>`;
    const host = this.container.querySelector<HTMLElement>('#mtr-drawer-host');
    if (host) this.bindDrawerHost(host);
    await this.loadAll();
    this.draw();
    this.patchDrawer();
    if (openLead) void this.openDrawerById(openLead);
  }

  private async loadAll(): Promise<void> {
    const [vacancy, rates, std, leads, tenants, contacts] = await Promise.all([
      fetch('/api/mtr/vacancy').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/mtr/rates').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/mtr/standard-rates').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/mtr/leads').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/mtr/tenants').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/mtr/contacts').then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.vacancy = vacancy; this.rates = rates; this.standardRates = std; this.leadsData = leads; this.tenantsData = tenants; this.contactsData = contacts;
  }
  private async reload(kind: 'vacancy' | 'rates' | 'standard-rates' | 'leads' | 'tenants' | 'contacts'): Promise<void> {
    const j = await fetch(`/api/mtr/${kind}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (kind === 'vacancy') this.vacancy = j;
    else if (kind === 'rates') this.rates = j;
    else if (kind === 'standard-rates') this.standardRates = j;
    else if (kind === 'leads') this.leadsData = j;
    else if (kind === 'tenants') this.tenantsData = j;
    else this.contactsData = j;
  }
  // R116: the roster re-fetch keeps what is on screen when it fails (reload('tenants') blanked the tab)
  private async reloadTenants(): Promise<boolean> {
    const j = await fetch('/api/mtr/tenants').then(r => r.ok ? r.json() : null).catch(() => null);
    if (j && Array.isArray(j.tenants)) { this.tenantsData = j; return true; }
    return false;
  }
  // R116: swap a tenant write's answer (the tenant view) into the roster
  private applyTenant(v: any): void {
    if (!v || !v.id || !this.tenantsData) return;
    const arr: any[] = Array.isArray(this.tenantsData.tenants) ? this.tenantsData.tenants : (this.tenantsData.tenants = []);
    const i = arr.findIndex((x: any) => x && x.id === v.id);
    if (i >= 0) arr[i] = { ...arr[i], ...v }; else arr.unshift(v);
  }
  private async post(path: string, body: any): Promise<any> {
    const r = await fetch(`/api/mtr${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) alert(j.error || `request failed (${r.status})${j.why ? ` — ${j.why}` : ''}`);
    return r.ok ? j : null;
  }
  private async put(path: string, body: any): Promise<any> {
    const r = await fetch(`/api/mtr${path}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) alert(j.error || `request failed (${r.status})${j.why ? ` — ${j.why}` : ''}`);
    return r.ok ? j : null;
  }
  // R114: the raw form — callers that need the status (409 'stale') or want to
  // word their own error use this; never alerts by itself.
  private async send(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: any): Promise<{ ok: boolean; status: number; j: any }> {
    try {
      const r = await fetch(`/api/mtr${path}`, {
        method,
        headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      return { ok: r.ok, status: r.status, j: j || {} };
    } catch {
      return { ok: false, status: 0, j: { error: 'Network error — check your connection and try again.' } };
    }
  }
  private async del(path: string): Promise<any> {
    const r = await this.send('DELETE', path);
    if (!r.ok) alert(r.j.error || `request failed (${r.status})`);
    return r.ok ? r.j : null;
  }
  // Keeps the board on failure (reload('leads') would blank it).
  private async reloadLeads(): Promise<boolean> {
    const j = await fetch('/api/mtr/leads').then(r => r.ok ? r.json() : null).catch(() => null);
    if (j && Array.isArray(j.leads)) { this.leadsData = j; return true; }
    return false;
  }
  private leadById(id: string | null): any {
    if (!id) return null;
    return (this.leadsData?.leads || []).find((l: any) => l.id === id) || null;
  }
  // Swap in the lead view a write answered with (POST /leads/:id, note PUT /
  // DELETE, doc upload) — no full reload needed.
  private replaceLead(v: any): void {
    if (!v || !v.id || !this.leadsData) return;
    const arr: any[] = Array.isArray(this.leadsData.leads) ? this.leadsData.leads : (this.leadsData.leads = []);
    const i = arr.findIndex((x: any) => x.id === v.id);
    // R116 #7: a write that answered without each tour's task (notes, checks…) keeps the copy we had
    if (i >= 0 && !v.tour_tasks && Array.isArray(v.tours)) {
      const old = new Map(((arr[i] && arr[i].tours) || []).map((t: any) => [t.id, t.task]));
      for (const t of v.tours) if (t && t.task === undefined && old.has(t.id)) t.task = old.get(t.id);
    }
    if (i >= 0) arr[i] = v; else arr.push(v);
  }
  private async afterLeadWrite(v: any): Promise<void> {
    if (v && v.id && Array.isArray(v.history)) this.replaceLead(v);
    else await this.reloadLeads();
    this.draw();
    this.patchDrawer();
  }
  private stages(): string[] {
    const s = this.leadsData?.stages;
    return Array.isArray(s) && s.length ? s : STAGES;
  }
  private stageLabel(s: any): string {
    const t = String(s || '').replace(/_/g, ' ');
    return t ? t[0].toUpperCase() + t.slice(1) : '—';
  }
  private me(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim().toLowerCase(); } catch { return ''; }
  }
  private myRole(): string {
    try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; }
  }
  // The server sends can_edit per note (and enforces it on PUT / DELETE); the
  // local rule is only a fallback for an older payload.
  private canEditNote(n: any): boolean {
    if (typeof n?.can_edit === 'boolean') return n.can_edit;
    const role = this.myRole();
    if (role === 'admin' || role === 'director') return true;
    const me = this.me();
    return !!(n?.by && me && String(n.by).toLowerCase() === me);
  }
  private async loadPeople(): Promise<void> {
    if (this.people || this.peopleLoading) return;
    this.peopleLoading = true;
    const j = await fetch('/api/tasks/assignees').then(r => r.ok ? r.json() : null).catch(() => null);
    this.peopleLoading = false;
    // tolerate failure: no names just means the email's local part is shown
    this.people = Array.isArray(j?.people)
      ? j.people.filter((p: any) => p && p.email).map((p: any) => ({ email: String(p.email), full_name: String(p.full_name || p.email) }))
      : [];
    if (this.people.length && this.drawerLeadId) this.patchDrawer();
  }
  private whoName(by: any): string {
    if (!by) return '';
    const v = String(by);
    if (v === 'dev') return 'Local dev';
    const p = (this.people || []).find(x => x.email.toLowerCase() === v.toLowerCase());
    return p && p.full_name && !p.full_name.includes('@') ? p.full_name : v.split('@')[0];
  }
  private fmtWhen(s: any): string {
    const t = Date.parse(String(s || ''));
    if (!Number.isFinite(t)) return '';
    const d = new Date(t), now = new Date();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
    return `${d.toLocaleDateString('en-US', opts)}, ${time}`;
  }
  private ago(s: any): string {
    const t = Date.parse(String(s || ''));
    if (!Number.isFinite(t)) return '';
    const m = (Date.now() - t) / 60000;
    if (m < 1) return 'just now';
    if (m < 60) return `${Math.floor(m)}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    if (m < 10080) return `${Math.floor(m / 1440)}d ago`;
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: new Date(t).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
  }
  private safeUrl(u: any): string {
    if (typeof u !== 'string' || !u.trim()) return '';
    try { const x = new URL(u.trim()); return (x.protocol === 'https:' || x.protocol === 'http:') ? x.href : ''; } catch { return ''; }
  }

  // ── shared formatting ───────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private fmtD(s: string | null): string {
    if (!s) return '—';
    const d = new Date(s + 'T00:00:00Z');
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  }
  private fmtFull(s: string | null): string {
    if (!s) return '—';
    const d = new Date(s + 'T00:00:00Z');
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  }
  private money(n: any): string { return n == null ? '—' : '$' + Math.round(Number(n)).toLocaleString(); }
  private effRateOf(listingId: string, m: number): { rate: number | null; source: string } {
    const row = (this.rates?.listings || []).find((r: any) => r.listing_id === String(listingId));
    const mo = row?.months?.find((x: any) => x.m === m);
    if (mo?.manual_rate != null) return { rate: mo.manual_rate, source: 'manual' };
    if (mo?.suggested_mtr != null) {
      return { rate: mo.suggested_mtr, source: mo.suggested_source === 'standard' ? 'standard-fallback' : 'AirROI-suggested' };
    }
    // R78: a lead can name any active property — outside the MTR set there is
    // no rates row, so fall back to the company by-bedroom standard rate.
    // Prefer the per-listing rate the /leads payload already carries (review:
    // the separate standard-rates fetch can fail while /leads succeeded, and
    // the picker right above shows this exact number).
    const ap = (this.leadsData?.all_properties || []).find((x: any) => String(x.listing_id) === String(listingId));
    const key = ap?.bedrooms != null ? String(Math.max(0, Math.min(5, Math.round(Number(ap.bedrooms))))) : null;
    const apRate = Number(ap?.standard_rate);
    if (apRate > 0) return { rate: apRate, source: `standard ${key ?? '?'}br` };
    const std = this.standardRates?.by_bedroom;
    if (key != null && std) {
      const r = Number(std[key]);
      if (r > 0) return { rate: r, source: `standard ${key}br` };
    }
    return { rate: null, source: mo ? 'no projection' : 'no data' };
  }
  // why-MTR chip label for a vacancy row's mtr_source.
  private mtrSourceLabel(src: string | null | undefined): string {
    return src === 'forced' ? 'forced on (always)'
      : src === 'type' ? 'typed mtr'
      : src === 'min_nights' ? 'min ≥ 28 nights'
      : (src || 'unknown');
  }
  private listingName(id: string | null): string {
    if (!id) return '—';
    const sid = String(id);
    // R78: leads may reference non-MTR-set homes — resolve from the full
    // roster first, then the vacancy-radar set.
    const ap = (this.leadsData?.all_properties || []).find((x: any) => String(x.listing_id) === sid);
    if (ap) return ap.name;
    const p = (this.vacancy?.properties || []).find((x: any) => x.listing_id === sid);
    return p ? p.name : sid;
  }

  // ── headline KPIs ───────────────────────────────────────────
  // Four headline tiles drawn from already-loaded data: vacancy pressure split
  // by mtr_mode (R48 — a vacant MTR-only home has no STR fallback), lead
  // pipeline, and rate coverage. No new fetching/computation.
  private renderKpis(): string {
    const props: any[] = this.vacancy?.properties || [];
    const ded = props.filter(p => p.mtr_mode === 'dedicated');
    const seas = props.filter(p => p.mtr_mode !== 'dedicated');
    const vacNow = (list: any[]) => list.filter(p => p.status === 'vacant_now').length;
    const soon = (list: any[]) => list.filter(p => p.status !== 'vacant_now' && typeof p.days_until_vacant === 'number' && p.days_until_vacant < 30).length;
    const dedVac = vacNow(ded), seasVac = vacNow(seas);

    const leads: any[] = this.leadsData?.leads || [];
    const openLeads = leads.filter(l => l.stage !== 'lost' && l.stage !== 'ended').length;

    const rateRows: any[] = (this.rates?.listings || []).filter((r: any) => r.in_mtr_set !== false);   // review: the KPI counts the MTR SET — the rates tab lists the whole portfolio now
    const priced = rateRows.filter(r => (r.months || []).some((m: any) => m.manual_rate != null || m.suggested_mtr != null)).length;

    const leadTone = openLeads > 0 ? 'tone-good' : '';

    return `
      <div class="kpi-grid">
        <div class="kpi-card ${dedVac > 0 ? 'tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">MTR-only vacant</span><span class="kpi-card-ic">${icon('home', 17)}</span></div>
          <div class="kpi-card-value">${dedVac}</div>
          <div class="kpi-card-cap">${soon(ded)} more within 30 days · of ${ded.length} dedicated</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Seasonal vacant</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
          <div class="kpi-card-value">${seasVac}</div>
          <div class="kpi-card-cap">${soon(seas)} more within 30 days · of ${seas.length} trying for MTR</div>
        </div>
        <div class="kpi-card ${leadTone}">
          <div class="kpi-card-top"><span class="kpi-card-label">Open leads</span><span class="kpi-card-ic">${icon('users', 17)}</span></div>
          <div class="kpi-card-value">${openLeads}</div>
          <div class="kpi-card-cap">in pipeline · of ${leads.length} on file</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Rates set</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value">${priced}</div>
          <div class="kpi-card-cap">of ${rateRows.length} MTR properties priced</div>
        </div>
      </div>`;
  }

  // ── shell ───────────────────────────────────────────────────
  private draw(): void {
    const body = document.getElementById('mtr-body');
    if (!body) return;
    // R114: an open ⋯ menu was MOVED out of the markup this redraw replaces —
    // put it back (and close it) first; a tab switch closes the card drawer.
    closeKebab();
    if (this.tab !== 'leads' && this.drawerLeadId) { this.drawerLeadId = null; this.noteDraft = ''; this.editNote = null; this.patchDrawer(); }
    const tabs = TABS;
    const live = this.tab === 'inbox' || this.tab === 'agent' || this.tab === 'holds';
    body.innerHTML = `
      <style>
        .mtr-head { display:flex; align-items:baseline; gap:12px; margin-bottom:8px; }
        .mtr-head h2 { margin:0; font-size:17px; font-weight:600; letter-spacing:-0.015em; color:var(--text); }
        .mtr-head .sub { font-size:12px; color:var(--text-muted); line-height:1.5; }
        .mtr-tabs { display:flex; gap:2px; margin:12px 0 18px; border-bottom:1px solid var(--border); overflow-x:auto; scrollbar-width:none; }
        .mtr-tabs::-webkit-scrollbar { display:none; }
        .mtr-tab { flex:none; white-space:nowrap; padding:8px 15px; font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; border:none; background:none; border-bottom:2px solid transparent; border-radius:8px 8px 0 0; transition:color .12s ease, background .12s ease, border-color .12s ease; }
        .mtr-tab:hover { color:var(--text); background:var(--bg-subtle); }
        .mtr-tab.on { color:var(--text); border-bottom-color:var(--text); background:none; }
        .mtr-basis { font-size:11px; color:var(--text-faint, #9ca3af); margin:6px 0 14px; line-height:1.55; }
        .mtr-sec { display:flex; align-items:baseline; gap:8px; margin:16px 0 8px; font-size:13.5px; font-weight:600; color:var(--text); letter-spacing:-0.01em; }
        .mtr-sec .mtr-propsub { font-weight:400; }
        .mtr-card { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); }
        table.mtr-tbl { width:100%; border-collapse:collapse; }
        table.mtr-tbl th { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); text-align:left; padding:11px 12px; border-bottom:1px solid var(--border); white-space:nowrap; }
        table.mtr-tbl td { font-size:12.5px; padding:11px 12px; border-bottom:1px solid var(--border-soft, #f1f5f9); vertical-align:middle; font-variant-numeric:tabular-nums; }
        table.mtr-tbl tbody tr { transition:background .1s ease; }
        table.mtr-tbl tbody tr:hover { background:var(--bg-subtle); }
        table.mtr-tbl tr:last-child td { border-bottom:none; }
        .mtr-pill { display:inline-block; font-size:10px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill, 999px); white-space:nowrap; letter-spacing:0.02em; font-variant-numeric:tabular-nums; }
        .mtr-chip { display:inline-block; font-size:10px; font-weight:500; border:1px solid var(--border); border-radius:var(--radius-pill, 999px); padding:3px 9px; cursor:pointer; color:var(--text-muted); background:var(--card, #fff); transition:border-color .12s ease, color .12s ease, background .12s ease; }
        .mtr-chip:hover { border-color:var(--text-muted); color:var(--text); }
        .mtr-chip.ok { border-color:var(--success); color:var(--success-text, #166534); background:var(--success-soft); }
        .mtr-chip.missing { border-style:dashed; }
        .mtr-prop { font-weight:600; color:var(--text); }
        .mtr-propsub { font-size:11px; color:var(--text-muted); }
        .mtr-why { display:inline-block; font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; padding:2px 7px; border-radius:var(--radius-pill, 999px); background:var(--bg-subtle); color:var(--text-muted); margin-top:4px; }
        .mtr-srctag { display:inline-block; font-size:8px; font-weight:700; line-height:1; padding:2px 5px; border-radius:5px; vertical-align:middle; margin-left:3px; letter-spacing:0.03em; text-transform:uppercase; }
        .mtr-srctag.std { background:var(--warning-soft); color:var(--warning-text); }
        .mtr-inline input, .mtr-inline select { padding:5px 8px; border:1px solid var(--border); border-radius:8px; font-size:11px; background:var(--card, #fff); color:var(--text); }
        .mtr-inline input:focus, .mtr-inline select:focus { outline:none; border-color:var(--text-muted); }
        .mtr-inline button { padding:5px 12px; border:1px solid var(--border); border-radius:8px; font-size:11px; font-weight:600; background:var(--card, #fff); color:var(--text); cursor:pointer; transition:border-color .12s ease, background .12s ease; }
        .mtr-inline button:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .mtr-grid-cell { text-align:right; cursor:pointer; font-variant-numeric:tabular-nums; transition:background .1s ease; border-radius:6px; }
        .mtr-grid-cell:hover { outline:1px solid var(--border); outline-offset:-1px; background:var(--bg-subtle); }
        .mtr-grid-cell.fav { background:var(--success-soft); }
        .mtr-grid-cell .man { font-weight:700; color:var(--text); }
        .mtr-grid-cell .sug { color:var(--text-muted); }
        .mtr-kanban { display:flex; gap:12px; overflow-x:auto; padding-bottom:8px; align-items:stretch; }
        .mtr-col { min-width:215px; width:215px; flex:none; background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius); padding:10px; display:flex; flex-direction:column; transition:background .12s ease, box-shadow .12s ease; }
        .mtr-col.over { background:var(--accent-soft); box-shadow:inset 0 0 0 2px var(--accent-border); }
        .mtr-col h4 { margin:0 0 10px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); display:flex; justify-content:space-between; align-items:center; }
        .mtr-col-empty { font-size:11px; color:var(--text-faint, #9ca3af); padding:12px 6px; text-align:center; border:1px dashed transparent; border-radius:var(--radius-sm); transition:border-color .12s ease; }
        .mtr-kanban.dragging .mtr-col-empty { border-color:var(--border-strong); }
        .mtr-col.over .mtr-col-empty { border-color:var(--accent-border); color:var(--accent); }
        .mtr-lead { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:11px 12px; margin-bottom:9px; font-size:12px; transition:box-shadow .12s ease, border-color .12s ease, opacity .12s ease; cursor:pointer; }
        .mtr-lead[draggable="true"] { cursor:grab; }
        .mtr-lead[draggable="true"]:active { cursor:grabbing; }
        .mtr-lead:hover { border-color:var(--border-strong); box-shadow:var(--shadow-hover); }
        .mtr-lead:focus-visible { outline:none; box-shadow:var(--focus-ring); border-color:var(--accent); }
        .mtr-lead.dragging { opacity:.45; }
        .mtr-lead.saving { opacity:.7; }
        .mtr-lead-top { display:flex; align-items:flex-start; gap:6px; }
        .mtr-lead-top .mtr-lead-name { flex:1; min-width:0; overflow-wrap:anywhere; padding-top:2px; }
        .mtr-lead-top .kb-btn { margin:-3px -6px 0 0; color:var(--text-muted); }
        .mtr-lead-name { font-weight:600; font-size:12.5px; color:var(--text); }
        .mtr-lead-meta { font-size:11px; color:var(--text-muted); margin-top:3px; }
        .mtr-checks { display:flex; gap:5px; margin-top:8px; }
        .mtr-check { width:21px; height:21px; border-radius:7px; border:1px solid var(--border); font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); background:var(--card, #fff); transition:border-color .12s ease, background .12s ease; padding:0; font-family:inherit; }
        .mtr-check:hover { border-color:var(--text-muted); }
        .mtr-check.done { background:var(--success-soft); border-color:var(--success); color:var(--success-text, #166534); }
        .mtr-pay { display:flex; flex-wrap:wrap; gap:5px; margin-top:8px; }
        .mtr-pay .p { font-size:9px; font-weight:600; border-radius:6px; padding:3px 7px; cursor:pointer; border:1px solid transparent; font-variant-numeric:tabular-nums; }
        .mtr-pay .p.paid { background:var(--success-soft); color:var(--success-text, #166534); }
        .mtr-pay .p.due { background:var(--bg-subtle); color:var(--text-muted); border-color:var(--border); }
        .mtr-pay .p.late { background:var(--danger-soft); color:var(--danger-text); }
        .mtr-lead-notes { margin:8px -6px 0; padding:4px 6px; border-radius:var(--radius-sm); font-size:11px; line-height:1.45; color:var(--text-muted); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; white-space:normal; overflow-wrap:anywhere; cursor:pointer; }
        .mtr-lead-notes:hover { background:var(--bg-subtle); color:var(--text); }
        .mtr-lead-notes .n { font-weight:600; color:var(--text); }
        .mtr-lead-notes.empty { color:var(--text-faint); }
        .mtr-lead-notes.empty:hover { color:var(--accent); }
        .mtr-lead-actions { display:flex; gap:10px; margin-top:9px; font-size:10.5px; font-weight:600; }
        .mtr-lead-actions a { cursor:pointer; color:var(--accent); transition:opacity .12s ease; }
        .mtr-lead-actions a:hover { opacity:0.7; }
        .mtr-lead-actions a.danger { color:var(--danger); }
        .mtr-form { display:flex; flex-wrap:wrap; gap:10px; align-items:flex-end; padding:16px; margin-bottom:16px; }
        .mtr-form label { display:flex; flex-direction:column; gap:4px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mtr-form input, .mtr-form select { padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:var(--card, #fff); color:var(--text); }
        .mtr-form input:focus, .mtr-form select:focus { outline:none; border-color:var(--text-muted); }
        .mtr-form button { padding:8px 16px; border:none; border-radius:8px; background:var(--text); color:#fff; font-size:12px; font-weight:600; cursor:pointer; transition:opacity .12s ease; }
        .mtr-form button:hover { opacity:0.88; }
        .mtr-modal-bg { position:fixed; inset:0; background:rgba(15,23,42,0.5); backdrop-filter:blur(2px); z-index:200; display:flex; align-items:center; justify-content:center; }
        .mtr-modal { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); width:min(680px, 92vw); max-height:86vh; display:flex; flex-direction:column; padding:20px; gap:12px; }
        .mtr-modal h3 { margin:0; font-size:14px; font-weight:600; color:var(--text); letter-spacing:-0.01em; }
        .mtr-modal textarea { flex:1; min-height:320px; font-family:var(--font-mono, ui-monospace, monospace); font-size:11.5px; line-height:1.5; padding:12px; border:1px solid var(--border); border-radius:8px; resize:vertical; background:var(--card, #fff); color:var(--text); }
        .mtr-modal .subj { font-family:var(--font-mono, ui-monospace, monospace); font-size:11.5px; padding:9px 12px; border:1px solid var(--border); border-radius:8px; background:var(--bg-subtle); }
        .mtr-verdict { font-size:10px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill, 999px); font-variant-numeric:tabular-nums; letter-spacing:0.02em; }
        /* R114: phone — one column ≈ the viewport, snap between columns, 36px tap targets on the cards */
        @media (max-width: 640px) {
          .mtr-kanban { scroll-snap-type:x proximity; gap:10px; }
          .mtr-col { width:auto; min-width:0; flex:0 0 min(80vw, 300px); scroll-snap-align:start; }
          .mtr-lead-top .kb-btn { min-width:36px; height:36px; margin:-8px -8px 0 0; }
          .mtr-check { width:36px; height:36px; font-size:11px; border-radius:9px; }
          .mtr-checks { gap:6px; }
          .mtr-pay .p { display:inline-flex; align-items:center; min-height:36px; padding:0 10px; font-size:10px; }
          .mtr-lead-notes { padding:9px 6px; }
          .mtr-lead-actions a { display:inline-flex; align-items:center; min-height:36px; font-size:12px; }
        }
      </style>
      <div class="dash-head">
        <h1>MTR Operations</h1>
        <p>Lead inbox, vacancy radar, rates, pipeline, tenants and contacts — mid-term set: type 'mtr', tagged, or 28+ night minimum (${this.vacancy?.count ?? '?'} properties as of ${this.esc(this.vacancy?.as_of || '—')})</p>
      </div>
      ${this.renderKpis()}
      <div class="mtr-tabs">
        ${tabs.map(([k, label]) => `<button class="mtr-tab ${this.tab === k ? 'on' : ''}" data-mtr-tab="${k}">${label}${k === 'tenants' ? this.tenantsTab.tabBadge() : ''}</button>`).join('')}
      </div>
      <div id="mtr-tab-body">
        ${live ? '' : this.tab === 'vacancy' ? this.renderVacancy() : this.tab === 'rates' ? this.renderRates() : this.tab === 'leads' ? this.renderLeads() : this.tab === 'tenants' ? this.renderTenants() : this.renderContacts()}
      </div>`;
    this.bind();
    // R116: the active tab stays in view on a phone (the strip scrolls sideways; Tenants sat off the right edge)
    const tabStrip = body.querySelector<HTMLElement>('.mtr-tabs');
    const onTab = tabStrip?.querySelector<HTMLElement>('.mtr-tab.on');
    if (tabStrip && onTab && tabStrip.scrollWidth > tabStrip.clientWidth) tabStrip.scrollLeft = Math.max(0, onTab.offsetLeft - tabStrip.offsetLeft - 24);
    // R114: ⋯ menus (one delegated opener — idempotent) + the board's drag & drop
    bindKebabs(this.container);
    if (this.tab === 'leads') { this.bindBoard(body); void this.loadPeople(); }
    // mount AFTER bind() so MtrPage's [data-mtr-*] sweep never touches their DOM
    const tabBody = body.querySelector<HTMLElement>('#mtr-tab-body');
    if (tabBody && this.tab === 'inbox') void this.inbox.mount(tabBody).catch(e => console.error('[mtr] lead inbox mount failed', e));
    else if (tabBody && this.tab === 'agent') void this.agentPanel.mount(tabBody).catch(e => console.error('[mtr] leads agent mount failed', e));
    else if (tabBody && this.tab === 'holds') void this.holdsPanel.mount(tabBody).catch(e => console.error('[mtr] holds mount failed', e));
  }

  // ── tab 1: vacancy radar ────────────────────────────────────
  private renderVacancy(): string {
    if (!this.vacancy) return emptyState('Vacancy radar unavailable', 'GET /api/mtr/vacancy failed — check the server log.', '⚠️');
    const props = this.vacancy.properties || [];
    if (!props.length) return emptyState('No MTR properties', "Nothing is typed 'mtr', mtr-tagged, or set to a 28+ night minimum.", '🏠');
    const row = (p: any) => {
      const d = p.days_until_vacant;
      const vacantNow = p.status === 'vacant_now';
      // R48: a vacant dedicated (MTR-only) home has no STR fallback — oversize badge
      const pill = vacantNow
        ? (p.mtr_mode === 'dedicated'
          ? `<span class="pill pill-red" style="font-size:12px; font-weight:700; padding:6px 13px; letter-spacing:0.05em;" title="${this.esc(p.note || (p.vacant_from ? `last tenant left ${p.vacant_from}` : ''))}">● VACANT</span>`
          : `<span class="pill pill-red" title="${this.esc(p.note || (p.vacant_from ? `last tenant left ${p.vacant_from}` : ''))}">VACANT NOW</span>`)
        : d < 30
        ? `<span class="pill pill-amber">${d}d</span>`
        : `<span class="pill pill-gray">${d}d</span>`;
      const vacFrom = p.vacant_from
        ? `<span class="mono num">${this.fmtD(p.vacant_from)}</span><div class="mtr-propsub">${vacantNow ? 'tenant left' : 'tenant until'} ${this.fmtD(p.current_tenant_until)}</div>`
        : `<span style="color:var(--text-muted);" title="${this.esc(p.note || '')}">no booking history</span>`;
      const gap = p.next_booking_start
        ? `<span class="mono num">${p.gap_days}d</span> <span class="mtr-propsub">→ next booking ${this.fmtD(p.next_booking_start)}</span>`
        : `<span style="color:var(--success-text, #166534); font-weight:600;">open-ended</span>`;
      const chChip = (key: 'furnished_finder' | 'zillow', label: string) => {
        const c = p.channels?.[key];
        // R115: the office-entered listed rate (Calendar MTR card) shows even before a URL is saved
        const listedBadge = c?.listed_rate ? ` · <b title="listed rate${c.listed_rate_by ? ` — set by ${this.esc(String(c.listed_rate_by).split('@')[0])}` : ''}">listed $${Number(c.listed_rate).toLocaleString()}</b>` : '';
        if (!c?.url) return `<span class="mtr-chip missing" data-mtr-ch="${p.listing_id}|${key}" title="no URL — click to add it">+ ${label}${listedBadge}</span>`;
        const priceBadge = (c.scraped_price ? ` · <b>$${Number(c.scraped_price).toLocaleString()}/mo</b>` : '') + listedBadge;
        return `<span class="mtr-chip ok" data-mtr-ch="${p.listing_id}|${key}" title="${this.esc(c.url)} — click to edit">${label} ${c.verified ? '✓' : '·unverified'}${priceBadge}</span><button class="mtr-chip" data-mtr-scrape="${p.listing_id}|${key}" title="Pull the current listed price from the page">↻ price</button>`;
      };
      const pc = this.priceResults[p.listing_id];
      const verdictChip = pc ? `
        <span class="mtr-verdict mono num" style="background:${pc.verdict === 'good' ? 'var(--success-soft)' : pc.verdict === 'close' ? 'var(--warning-soft)' : 'var(--danger-soft)'}; color:${pc.verdict === 'good' ? 'var(--success-text, #166534)' : pc.verdict === 'close' ? 'var(--warning-text)' : 'var(--danger-text)'};"
          title="${this.esc(pc.basis)} · ${this.esc(pc.threshold_note)}">${pc.verdict.toUpperCase()} · ${pc.pct_of_expected}% of ${this.money(pc.effective_rate)}</span>` : '';
      const defMonth = new Date(((p.vacant_from && p.vacant_from > this.vacancy.as_of) ? p.vacant_from : this.vacancy.as_of) + 'T00:00:00Z').getUTCMonth() + 1;
      return `
        <tr>
          <td><div class="mtr-prop">${this.esc(p.name)}</div><div class="mtr-propsub">min ${p.min_nights ?? '?'} nights</div><div class="mtr-why" title="why this property is in the MTR set (overrides respected)">why MTR: ${this.esc(this.mtrSourceLabel(p.mtr_source))}</div></td>
          <td>${vacFrom}</td>
          <td>${pill}</td>
          <td>${gap}</td>
          <td><div style="display:flex; gap:4px; flex-wrap:wrap;">${chChip('furnished_finder', 'FF')}${chChip('zillow', 'Zillow')}</div></td>
          <td class="mtr-inline" style="white-space:nowrap;">
            <select data-mtr-pcm="${p.listing_id}">${MONTHS.map((mn, i) => `<option value="${i + 1}" ${i + 1 === defMonth ? 'selected' : ''}>${mn}</option>`).join('')}</select>
            <input type="number" data-mtr-pcp="${p.listing_id}" placeholder="$/mo" style="width:76px;">
            <button data-mtr-pc="${p.listing_id}">Check</button>
            ${verdictChip}
          </td>
        </tr>`;
    };
    // R48: board split by mtr_mode — dedicated (MTR-only) first, seasonal after.
    const ded = props.filter((p: any) => p.mtr_mode === 'dedicated');
    const seas = props.filter((p: any) => p.mtr_mode !== 'dedicated');
    const table = (list: any[]) => `
      <div class="mtr-card" style="overflow-x:auto;">
        <table class="mtr-tbl">
          <thead><tr><th>Property</th><th>Vacant from</th><th>Days out</th><th>Gap to next booking</th><th>Channels</th><th>Check a price</th></tr></thead>
          <tbody>${list.map(row).join('')}</tbody>
        </table>
      </div>`;
    const section = (title: string, sub: string, list: any[]) => `
      <div class="mtr-sec">${title} <span class="mono num">${list.length}</span><span class="mtr-propsub">${sub}</span></div>
      ${list.length ? table(list) : `<div class="mtr-basis" style="margin-top:2px;">none — set a property's MTR mode in Property Settings</div>`}`;
    return `
      <div class="mtr-basis">Sorted by urgency · ${this.esc(this.vacancy.basis)} · overrides respected (always/never) · mode: explicit per property (Property Settings), else typed 'mtr' = MTR-only · price check: good ≥ 100% of effective rate (manual, else AirROI-suggested, else by-bedroom standard), close ≥ 95% — within 5% = acceptable (owner rule)</div>
      ${section('🏠 MTR-only properties', 'dedicated — vacancy here has no STR fallback', ded)}
      ${section('🎯 Trying for MTR (seasonal)', 'STR homes taking mid-term stays when the season favors it', seas)}`;
  }

  // ── tab 2: rates grid ───────────────────────────────────────
  private renderRates(): string {
    if (!this.rates) return emptyState('Rates unavailable', 'GET /api/mtr/rates failed — check the server log.', '⚠️');
    const list = this.rates.listings || [];
    if (!list.length) return emptyState('No MTR properties', "Nothing is typed 'mtr', mtr-tagged, or set to a 28+ night minimum.", '🏠');
    const rows = list.map((r: any) => {
      const cells = r.months.map((mo: any) => {
        const stdTag = (mo.manual_rate == null && mo.suggested_source === 'standard')
          ? `<span class="mtr-srctag std" title="standard ${r.bedrooms ?? '?'}br fallback — no AirROI projection for this listing">std</span>` : '';
        const val = mo.manual_rate != null
          ? `<span class="man mono num" title="manual rate — click to edit/clear">${Math.round(mo.manual_rate).toLocaleString()}</span>`
          : mo.suggested_mtr != null
          ? (mo.suggested_source === 'standard'
            ? `<span class="sug mono num" title="standard ${r.bedrooms ?? '?'}br rate $${mo.suggested_mtr.toLocaleString()}/mo (no AirROI projection — by-bedroom fallback) — click to set a manual rate">${mo.suggested_mtr.toLocaleString()}</span>${stdTag}`
            : `<span class="sug mono num" title="suggested: AirROI expected STR revenue ${this.money(mo.str_expected)} for ${MONTHS[mo.m - 1]}, rounded to $50 — click to set a manual rate">${mo.suggested_mtr.toLocaleString()}</span>`)
          : `<span class="sug" title="${this.esc(r.projection_note || 'no AirROI projection')}">—</span>`;
        return `<td class="mtr-grid-cell ${mo.favorable ? 'fav' : ''}" data-mtr-rate="${r.listing_id}|${mo.m}" ${mo.favorable ? `title="MTR-favorable: ${MONTHS[mo.m - 1]} share ${(mo.monthly_share * 100).toFixed(1)}% of annual < 6.7% (80% of flat 1/12)"` : ''}>${val}</td>`;
      }).join('');
      return `
        <tr>
          <td><div class="mtr-prop">${this.esc(r.name)}${r.in_mtr_set ? ' <span style="font-size:9px; font-weight:700; color:#0e7490; background:#ecfeff; border-radius:999px; padding:1px 6px; vertical-align:middle;" title="In the MTR set (typed/tagged/28+ min stay) — the rest of the portfolio is listed too so any home can quote a monthly rate">MTR</span>' : ''}</div><div class="mtr-propsub">${r.has_projection ? `AirROI expected ${this.money(r.expected_annual_str)}/yr STR` : this.esc(r.projection_note || 'no projection')}</div></td>
          ${cells}
        </tr>`;
    }).join('');
    return `
      ${this.renderAirroiBanner()}
      ${this.renderStandardRatesCard()}
      <div class="mtr-basis">All values are monthly MTR rates in $. Suggested = AirROI expected STR revenue for that month rounded to $50 (an MTR replacing STR must clear what STR would earn); when no AirROI projection exists, falls back to the by-bedroom Standard MTR rate (tagged <span class="mtr-srctag std">std</span>). Favorable = seasonality below 80% of flat share (1/12). AirROI ${this.rates.year} projection cache generated ${this.esc(this.rates.generated_at || 'never')}. Click any cell to set/clear a manual rate (bold = manual, gray = suggested).</div>
      <div class="mtr-card" style="overflow-x:auto;">
        <table class="mtr-tbl" style="min-width:1080px;">
          <thead><tr><th>Property</th>${MONTHS.map(m => `<th style="text-align:right;">${m}</th>`).join('')}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div class="mtr-basis" style="margin-top:8px;">
        <span style="display:inline-block; width:11px; height:11px; background:var(--success-soft); border:1px solid var(--border); border-radius:3px; vertical-align:-1px;"></span>
        MTR-favorable month &nbsp;·&nbsp; <b>bold</b> = manual rate &nbsp;·&nbsp; gray = AirROI suggestion &nbsp;·&nbsp; <span class="mtr-srctag std">std</span> = by-bedroom standard fallback (no AirROI projection)
      </div>`;
  }

  // R124 (Jesse: "The MTR rates are still not corrected"): when AirROI gave nothing, say so and why,
  // instead of quietly showing the by-bedroom fallback as if it were a suggestion.
  private renderAirroiBanner(): string {
    const r = this.rates || {};
    const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' }) : '');
    const box = (html: string) => `<div class="mtr-card" data-mtr-airroi-banner style="background:var(--warning-soft); color:var(--warning-text); font-size:13px; line-height:1.5; margin-bottom:10px;">${html}</div>`;
    const rf = r.refresh;
    if (rf && !rf.ok) {
      const top = (rf.reasons || [])[0];
      return box(`<b>The AirROI refresh on ${this.esc(day(rf.at))} got ${rf.with_estimate ?? 0} of ${rf.eligible ?? '?'} estimates</b>${top ? `. AirROI said: "${this.esc(top.reason)}"` : ''}. ${rf.kept_from ? `The estimates from ${this.esc(day(rf.kept_from))} are still in use.` : 'Until it works, the rates below are the by-bedroom standard rates.'} Fix the AirROI account (its key or credits) and the Monday refresh fills these in.`);
    }
    const c = r.cache;
    if (c && !c.listings) {
      return box(`<b>No AirROI estimates are cached</b>${c.generated_at ? ` (the refresh on ${this.esc(day(c.generated_at))} came back empty)` : ''}. Until the next refresh works, the rates below are the by-bedroom standard rates.`);
    }
    return '';
  }

  // Standard MTR rates by bedroom — company default fallback list (editable).
  private renderStandardRatesCard(): string {
    const std = this.standardRates || this.rates?.standard_rates || { by_bedroom: {}, reserves: 0 };
    const by = std.by_bedroom || {};
    const beds: Array<[string, string]> = [['0', 'Studio'], ['1', '1 bdr'], ['2', '2 bdr'], ['3', '3 bdr'], ['4', '4 bdr'], ['5', '5+ bdr']];
    const inputs = beds.map(([k, label]) => `
      <label>${label}<input type="number" min="0" data-mtr-std="${k}" value="${this.esc(by[k] ?? by[Number(k)] ?? '')}" style="width:72px;"></label>`).join('');
    return `
      <div class="mtr-card mtr-form" id="mtr-std-form" style="align-items:flex-end;">
        <div style="font-size:11px; font-weight:600; color:var(--text-muted); width:100%; margin-bottom:-2px;">Standard MTR rates by bedroom <span style="font-weight:400;">— company default $/mo used as a fallback when no AirROI suggestion exists (bedrooms &gt;5 clamp to 5+ bdr)</span></div>
        ${inputs}
        <label>Reserves<input type="number" min="0" data-mtr-std="reserves" value="${this.esc(std.reserves ?? '')}" style="width:80px;"></label>
        <button id="mtr-std-save">Save standard rates</button>
        ${this.stdSuggestHtml(std)}
      </div>`;
  }
  // R124: the placeholders say so, and our own leases suggest real rates (a person applies them)
  private stdSuggestHtml(std: any): string {
    const placeholder = std.placeholder ?? this.rates?.standard_placeholder;
    const sg = std.suggested;
    const names: Record<string, string> = { 0: 'Studio', 1: '1 bdr', 2: '2 bdr', 3: '3 bdr', 4: '4 bdr', 5: '5+ bdr' };
    const keys = sg && sg.by_bedroom ? Object.keys(sg.by_bedroom).sort() : [];
    const parts = keys.map(k => `${names[k] || k} ${this.money(sg.by_bedroom[k])} <span style="color:var(--text-muted);">(${sg.basis[k].n} lease${sg.basis[k].n === 1 ? '' : 's'})</span>`);
    return `<div style="width:100%; font-size:12px; line-height:1.5; margin-top:2px;" data-mtr-std-suggest>
      ${placeholder ? `<div style="color:var(--warning-text);">These are placeholders nobody has set yet, so every <span class="mtr-srctag std">std</span> rate below uses them.</div>` : ''}
      ${parts.length ? `<div>From our ${sg.leases} MTR lease${sg.leases === 1 ? '' : 's'} (median monthly rent, rounded to $50): ${parts.join(' · ')}. <button type="button" id="mtr-std-use" style="margin-left:6px;">Use these</button> <span style="color:var(--text-muted);">fills the boxes; nothing is saved until you press Save.</span></div>` : ''}
    </div>`;
  }

  // ── tab 3: leads kanban ─────────────────────────────────────
  private renderLeads(): string {
    if (!this.leadsData) return emptyState('Leads unavailable', 'GET /api/mtr/leads failed — check the server log.', '⚠️');
    const leads: any[] = this.leadsData.leads || [];
    const stages = this.stages();
    // R78: leads can land for ANY active property (all are potential MTRs), so
    // the picker is the full roster with the by-bedroom starting rate — not
    // just the current MTR set. Falls back to the vacancy-radar set if the
    // server predates all_properties.
    const pickerProps: any[] = this.leadsData.all_properties?.length ? this.leadsData.all_properties : (this.vacancy?.properties || []);
    const propOpts = pickerProps.map((p: any) =>
      `<option value="${p.listing_id}">${this.esc(p.name)}${p.standard_rate ? ` · from ${this.money(p.standard_rate)}/mo` : ''}</option>`).join('');
    const form = `
      <div class="mtr-card mtr-form" id="mtr-lead-form">
        <label>Name<input id="mtr-lf-name" placeholder="Tenant / company"></label>
        <label>Contact<input id="mtr-lf-contact" placeholder="email or phone"></label>
        <label>Property<select id="mtr-lf-prop"><option value="">—</option>${propOpts}</select></label>
        <label>Move-in<input id="mtr-lf-movein" type="date"></label>
        <label>Term (mo)<input id="mtr-lf-term" type="number" style="width:64px;"></label>
        <label>Offer $/mo<input id="mtr-lf-offer" type="number" style="width:90px;"></label>
        <button id="mtr-lf-add">Add lead</button>
      </div>`;
    const card = (l: any) => {
      const m = l.move_in ? new Date(l.move_in + 'T00:00:00Z').getUTCMonth() + 1 : null;
      const eff = (l.property_interest && m) ? this.effRateOf(l.property_interest, m) : { rate: null, source: '' };
      const pct = (l.offer_price != null && eff.rate) ? Math.round(l.offer_price / eff.rate * 1000) / 10 : null;
      const offerChip = l.offer_price == null ? ''
        : pct == null
        ? `<span class="mtr-verdict" style="background:var(--bg-subtle); color:var(--text-muted);" title="no effective rate to compare — ${this.esc(eff.source)}${l.property_interest ? '' : ' (no property set)'}${l.move_in ? '' : ' (no move-in month)'}">${this.money(l.offer_price)}/mo · vs ?</span>`
        : `<span class="mtr-verdict mono num" style="background:${pct >= 100 ? 'var(--success-soft)' : pct >= 95 ? 'var(--warning-soft)' : 'var(--danger-soft)'}; color:${pct >= 100 ? 'var(--success-text, #166534)' : pct >= 95 ? 'var(--warning-text)' : 'var(--danger-text)'};" title="offer ${this.money(l.offer_price)} vs ${this.esc(eff.source)} rate ${this.money(eff.rate)} for ${MONTHS[(m as number) - 1]} · within 5% = acceptable (owner rule)">${this.money(l.offer_price)} · ${pct}%</span>`;
      const checks = CHECKS.map(c => {
        const st = l.checks?.[c.key] || { done: false };
        return `<span class="mtr-check ${st.done ? 'done' : ''}" data-mtr-check="${l.id}|${c.key}" title="${c.label}: ${st.done ? 'done' : 'not done'}${st.note ? ` — ${this.esc(st.note)}` : ''}${st.url ? ' · doc attached' : ''} (click to toggle · open the card for notes + docs)">${c.short}</span>`;
      }).join('');
      const pays = (l.stage === 'active' || (l.payments || []).length) ? `
        <div class="mtr-pay">
          ${(l.payments || []).map((p: any) => `<span class="p ${p.status}" data-mtr-pay="${l.id}|${p.month}" title="${p.month}: ${p.status}${p.note ? ` — ${this.esc(p.note)}` : ''} (click cycles due→paid→late)">${p.month.slice(2)} ${p.status}</span>`).join('')}
          <span class="p due" data-mtr-payadd="${l.id}" title="add a payment month">+ mo</span>
        </div>` : '';
      // R114: ONE primary inline action (→ next stage, or ↺ Reopen on a lost /
      // ended card); every stage — backward, skip, lost, ended — is in ⋯ Move
      // to (the touch path; drag & drop is the mouse path).
      const idx = stages.indexOf(l.stage);
      const next = (idx >= 0 && idx < stages.indexOf('active')) ? stages[idx + 1] : null;
      const reopen = TERMINAL.includes(l.stage) ? String(l.reopen_to || 'contacted') : null;
      const items: KebabItem[] = [
        ...stages.map(s => ({
          icon: s === l.stage ? '✓' : '',
          label: s === l.stage ? `${this.stageLabel(s)} (current)` : this.stageLabel(s),
          attrs: { 'data-mtr-adv': `${l.id}|${s}` },
          disabled: s === l.stage,
          danger: TERMINAL.includes(s) && s !== l.stage,
        })),
        { divider: true },
        { icon: '📝', label: 'Add note', attrs: { 'data-mtr-open': `${l.id}|note` } },
        { icon: '📎', label: 'Attach doc', attrs: { 'data-mtr-doc': l.id } },
        { icon: '↗', label: 'Open details', attrs: { 'data-mtr-open': l.id } },
      ];
      const nCount = Number(l.notes_count) || 0;
      const latest = l.latest_note;
      const notesRow = nCount && latest
        ? `<div class="mtr-lead-notes" role="button" tabindex="0" data-mtr-open="${l.id}|note" title="Latest note — ${this.esc(latest.by ? this.whoName(latest.by) : 'author not recorded')} · ${this.esc(this.fmtWhen(latest.at))} (click for all notes)"><span class="n">💬 ${nCount}</span> · “${this.esc(latest.snippet)}”</div>`
        : `<div class="mtr-lead-notes empty" role="button" tabindex="0" data-mtr-open="${l.id}|note" title="Add a note to this lead">＋ Add a note</div>`;
      return `
        <div class="mtr-lead${this.moving.has(l.id) ? ' saving' : ''}" data-mtr-card="${l.id}" draggable="true" tabindex="0" title="Drag to another column, or ⋯ → Move to">
          <div class="mtr-lead-top">
            <div class="mtr-lead-name">${this.esc(l.name)}</div>
            ${kebabHtml(items, { title: 'Move to', tip: 'Move to another stage, add a note, attach a doc' })}
          </div>
          <div class="mtr-lead-meta">${this.esc(this.listingName(l.property_interest))}${l.move_in ? ` · in ${this.fmtD(l.move_in)}` : ''}${l.term_months ? ` · ${l.term_months}mo` : ''}</div>
          ${this.tours.cardChipHtml(l)}
          ${offerChip ? `<div style="margin-top:5px;">${offerChip}</div>` : ''}
          <div class="mtr-checks">${checks}</div>
          ${pays}
          ${notesRow}
          ${next || reopen ? `<div class="mtr-lead-actions">
            ${next ? `<a data-mtr-adv="${l.id}|${next}" data-mtr-via="next" title="Move to ${this.esc(this.stageLabel(next))}">→ ${this.esc(next.replace('_', ' '))}</a>` : ''}
            ${reopen ? `<a data-mtr-adv="${l.id}|${this.esc(reopen)}" title="Reopen — back to ${this.esc(this.stageLabel(reopen))}">↺ Reopen</a>` : ''}
          </div>` : ''}
        </div>`;
    };
    const cols = stages.map(s => {
      const inCol = leads.filter(l => l.stage === s);
      return `
        <div class="mtr-col" data-mtr-col="${s}">
          <h4>${s.replace('_', ' ')} <span class="mono num">${inCol.length}</span></h4>
          ${inCol.map(card).join('') || `<div class="mtr-col-empty">Drop a lead here</div>`}
        </div>`;
    }).join('');
    return `
      ${form}
      <div class="mtr-basis">${leads.length} lead${leads.length === 1 ? '' : 's'} on file (as of ${this.esc(this.leadsData.as_of)}) · drag a card to any column (or ⋯ → Move to) — backward moves are fine; click a card for its notes, checks and history · checks: B background, C credit, L lease, D deposit — click to toggle with a note · offer % = offer vs effective monthly rate for the move-in month (manual, else AirROI-suggested, else the company by-bedroom standard rate — used for homes outside the MTR set)</div>
      <div class="mtr-kanban">${cols}</div>
      <input type="file" id="mtr-doc-file" style="display:none;">`;
  }

  // ── tab 4: tenants in-house + rent tracking ─────────────────
  // R116 #4: the cards / notes / follow-ups / Edit sheet live in MtrTenantsTab.
  private renderTenants(): string {
    return this.tenantsTab.render();
  }

  // ── tab 5: contacts + outreach ──────────────────────────────
  private renderContacts(): string {
    if (!this.contactsData) return emptyState('Contacts unavailable', 'GET /api/mtr/contacts failed — check the server log.', '⚠️');
    const contacts: any[] = this.contactsData.contacts || [];
    const kinds = this.contactsData.kinds || ['insurance', 'housing_agency', 'relocation', 'other'];
    const form = `
      <div class="mtr-card mtr-form" id="mtr-ct-form">
        <label>Name<input id="mtr-cf-name" placeholder="contact name"></label>
        <label>Org<input id="mtr-cf-org"></label>
        <label>Kind<select id="mtr-cf-kind">${kinds.map((k: string) => `<option value="${k}">${k.replace('_', ' ')}</option>`).join('')}</select></label>
        <label>Email<input id="mtr-cf-email" type="email"></label>
        <label>Phone<input id="mtr-cf-phone"></label>
        <label>Notes<input id="mtr-cf-notes" style="min-width:160px;"></label>
        <button id="mtr-cf-add">Add contact</button>
      </div>`;
    const rows = contacts.map(c => `
      <tr>
        <td><div class="mtr-prop">${this.esc(c.name)}</div><div class="mtr-propsub">${this.esc(c.org || '')}</div></td>
        <td><span class="pill pill-gray">${this.esc((c.kind || 'other').replace('_', ' '))}</span></td>
        <td>${this.esc(c.email || '')}${c.email && c.phone ? '<br>' : ''}${this.esc(c.phone || '')}</td>
        <td>${c.last_outreach ? `<span class="mono num">${this.esc(String(c.last_outreach).slice(0, 10))}</span>` : '<span style="color:var(--text-muted);">never</span>'}</td>
        <td style="max-width:220px; overflow:hidden; text-overflow:ellipsis;" title="${this.esc(c.notes || '')}">${this.esc(c.notes || '')}</td>
        <td class="mtr-inline" style="white-space:nowrap;">
          <button data-mtr-draft="${c.id}">Draft outreach</button>
          <button data-mtr-ct-del="${c.id}" title="delete contact" style="color:var(--danger);">✕</button>
        </td>
      </tr>`).join('');
    return `
      ${form}
      <div class="mtr-basis">${contacts.length} contact${contacts.length === 1 ? '' : 's'} · 'Draft outreach' composes an email listing vacant + soon-vacant MTR homes with effective rates and booking links — draft only, nothing is ever sent; copying it stamps 'last outreach'</div>
      <div class="mtr-card" style="overflow-x:auto;">
        <table class="mtr-tbl">
          <thead><tr><th>Contact</th><th>Kind</th><th>Reach</th><th>Last outreach</th><th>Notes</th><th></th></tr></thead>
          <tbody>${rows || `<tr><td colspan="6" style="color:var(--text-muted); text-align:center; padding:24px;">No contacts yet — add insurance adjusters, housing agencies and relocation firms above.</td></tr>`}</tbody>
        </table>
      </div>`;
  }

  private showDraftModal(contactId: string, draft: any): void {
    const bg = document.createElement('div');
    bg.className = 'mtr-modal-bg';
    bg.innerHTML = `
      <div class="mtr-modal">
        <h3>Outreach draft — copy &amp; send from your own email (nothing is sent from here)</h3>
        <div class="mtr-basis" style="margin:0;">${this.esc(draft.basis || '')}</div>
        <div class="subj">Subject: ${this.esc(draft.subject)}</div>
        <textarea id="mtr-draft-body">${this.esc(draft.body)}</textarea>
        <div style="display:flex; gap:8px; justify-content:flex-end;">
          <button class="mtr-tab" id="mtr-draft-close" style="border:1px solid var(--border); border-radius:6px;">Close</button>
          <button id="mtr-draft-copy" style="padding:7px 16px; border:none; border-radius:6px; background:var(--text); color:#fff; font-size:12px; font-weight:600; cursor:pointer;">Copy (marks outreach)</button>
        </div>
      </div>`;
    document.body.appendChild(bg);
    const close = () => bg.remove();
    bg.addEventListener('click', (e) => { if (e.target === bg) close(); });
    bg.querySelector('#mtr-draft-close')?.addEventListener('click', close);
    bg.querySelector('#mtr-draft-copy')?.addEventListener('click', async () => {
      const body = (bg.querySelector('#mtr-draft-body') as HTMLTextAreaElement).value;
      try { await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${body}`); } catch (_) { /* clipboard blocked — still mark */ }
      await this.post('/outreach-mark', { contact_id: contactId });
      await this.reload('contacts');
      close();
      this.draw();
    });
  }

  // ── events ──────────────────────────────────────────────────
  private bind(): void {
    // R114: scoped to #mtr-body — the card drawer (#mtr-drawer-host, a sibling)
    // has its own delegated listeners and must never be swept here.
    const root: HTMLElement = document.getElementById('mtr-body') || this.container;
    const $ = (sel: string) => root.querySelector(sel) as HTMLInputElement | null;
    root.querySelectorAll<HTMLElement>('[data-mtr-tab]').forEach(el =>
      el.addEventListener('click', () => { this.tab = el.dataset.mtrTab as any; this.draw(); }));

    // vacancy: channel chips
    root.querySelectorAll<HTMLElement>('[data-mtr-ch]').forEach(el =>
      el.addEventListener('click', async () => {
        const [listing_id, channel] = el.dataset.mtrCh!.split('|');
        const cur = (this.vacancy?.properties || []).find((p: any) => p.listing_id === listing_id)?.channels?.[channel] || {};
        const url = prompt(`${channel === 'furnished_finder' ? 'Furnished Finder' : 'Zillow'} listing URL for this property (empty to remove):`, cur.url || '');
        if (url === null) return;
        const verified = url ? confirm('Mark this listing as verified live? (OK = verified, Cancel = unverified)') : false;
        if (await this.post('/channels', { listing_id, channel, url, verified })) {
          // Auto-pull the current listed price right after saving a URL.
          if (url) { const s = await this.post('/scrape-price', { listing_id, channel }); if (s && !s.ok && s.reason) alert(`Saved. Price not pulled — ${s.reason}`); }
          await this.reload('vacancy'); this.draw();
        }
      }));
    // vacancy: pull listed price from a channel URL
    root.querySelectorAll<HTMLElement>('[data-mtr-scrape]').forEach(el =>
      el.addEventListener('click', async () => {
        const [listing_id, channel] = el.dataset.mtrScrape!.split('|');
        el.textContent = '…';
        const s = await this.post('/scrape-price', { listing_id, channel });
        if (s && s.ok) { await this.reload('vacancy'); this.draw(); }
        else { alert(s?.reason || 'Could not pull the price.'); el.textContent = '↻ price'; }
      }));
    // vacancy: price check
    root.querySelectorAll<HTMLElement>('[data-mtr-pc]').forEach(el =>
      el.addEventListener('click', async () => {
        const id = el.dataset.mtrPc!;
        const month = ($(`[data-mtr-pcm="${id}"]`) as any)?.value;
        const price = ($(`[data-mtr-pcp="${id}"]`) as any)?.value;
        if (!price) { alert('Enter a monthly price to check.'); return; }
        const j = await this.post('/price-check', { listing_id: id, month: Number(month), price: Number(price) });
        if (j) { this.priceResults[id] = j; this.draw(); }
      }));

    // R124: fill the boxes from the lease-based suggestions (Save still decides)
    $('#mtr-std-use')?.addEventListener('click', () => {
      const sg = this.standardRates?.suggested;
      if (!sg || !sg.by_bedroom) return;
      for (const [k, v] of Object.entries(sg.by_bedroom)) {
        const inp = root.querySelector<HTMLInputElement>(`[data-mtr-std="${k}"]`);
        if (inp) inp.value = String(v);
      }
    });
    // rates: save standard rates by bedroom
    $('#mtr-std-save')?.addEventListener('click', async () => {
      const by_bedroom: Record<string, number> = {};
      root.querySelectorAll<HTMLInputElement>('[data-mtr-std]').forEach(inp => {
        const k = inp.dataset.mtrStd!;
        if (k === 'reserves') return;
        if (inp.value !== '') by_bedroom[k] = Number(inp.value);
      });
      const reservesEl = $('[data-mtr-std="reserves"]');
      const reserves = reservesEl && reservesEl.value !== '' ? Number(reservesEl.value) : undefined;
      const j = await this.put('/standard-rates', { by_bedroom, ...(reserves !== undefined ? { reserves } : {}) });
      if (j) {
        this.standardRates = j;
        // Re-derive grid fallbacks against the new standard rates (and keep the R124 suggestions).
        await Promise.all([this.reload('rates'), this.reload('standard-rates')]); this.draw();
      }
    });
    // rates: cell edit
    root.querySelectorAll<HTMLElement>('[data-mtr-rate]').forEach(el =>
      el.addEventListener('click', async () => {
        const [listing_id, mStr] = el.dataset.mtrRate!.split('|');
        const m = Number(mStr);
        const row = (this.rates?.listings || []).find((r: any) => r.listing_id === listing_id);
        const mo = row?.months?.find((x: any) => x.m === m);
        const cur = mo?.manual_rate ?? mo?.suggested_mtr ?? '';
        const v = prompt(`Manual MTR rate for ${this.esc(row?.name || listing_id)} · ${MONTHS[m - 1]} ($/month, empty clears the override${mo?.suggested_mtr != null ? `; suggestion is $${mo.suggested_mtr.toLocaleString()}` : ''}):`, String(cur));
        if (v === null) return;
        if (await this.post('/rates', { listing_id, month: m, rate: v === '' ? null : Number(v) })) {
          await this.reload('rates'); this.draw();
        }
      }));

    // leads: add
    $('#mtr-lf-add')?.addEventListener('click', async () => {
      const name = $('#mtr-lf-name')?.value?.trim();
      if (!name) { alert('Lead name is required.'); return; }
      const j = await this.post('/leads', {
        name,
        contact: $('#mtr-lf-contact')?.value || null,
        property_interest: $('#mtr-lf-prop')?.value || null,
        move_in: $('#mtr-lf-movein')?.value || null,
        term_months: $('#mtr-lf-term')?.value || null,
        offer_price: $('#mtr-lf-offer')?.value || null,
      });
      if (j) { await this.reload('leads'); this.draw(); }
    });
    // leads: any stage move (→ next, ↺ Reopen, every ⋯ Move to item). The ⋯
    // items are bound per element (the Kebab contract): an open menu is moved
    // to <body>, out of reach of any delegated listener.
    root.querySelectorAll<HTMLElement>('[data-mtr-adv]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, stage] = el.dataset.mtrAdv!.split('|');
        void this.moveLead(id, stage, { via: el.dataset.mtrVia || '' });
      }));
    // R116 #7: a Touring card with no tour booked → the Schedule-tour sheet
    root.querySelectorAll<HTMLElement>('[data-mtt-sched]').forEach(el =>
      el.addEventListener('click', () => { const l = this.leadById(el.dataset.mttSched!); if (l) this.tours.openSheet({ mode: 'add', leadId: l.id }); }));
    // leads: open the card drawer (notes row, ⋯ Add note / Open details)
    root.querySelectorAll<HTMLElement>('[data-mtr-open]').forEach(el => {
      const go = () => {
        const [id, what] = el.dataset.mtrOpen!.split('|');
        this.openDrawer(id, what === 'note');
      };
      el.addEventListener('click', go);
      el.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); go(); } });
    });
    // leads: checks cycle (with note prompt)
    root.querySelectorAll<HTMLElement>('[data-mtr-check]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, key] = el.dataset.mtrCheck!.split('|');
        void this.toggleCheck(id, key);
      }));
    // leads: payment chips cycle due→paid→late→due
    root.querySelectorAll<HTMLElement>('[data-mtr-pay]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, month] = el.dataset.mtrPay!.split('|');
        void this.cyclePayment(id, month);
      }));
    root.querySelectorAll<HTMLElement>('[data-mtr-payadd]').forEach(el =>
      el.addEventListener('click', () => { void this.addPaymentMonth(el.dataset.mtrPayadd!); }));
    // leads: verification doc upload
    root.querySelectorAll<HTMLElement>('[data-mtr-doc]').forEach(el =>
      el.addEventListener('click', () => { this.uploadDoc(el.dataset.mtrDoc!); }));

    // tenants (R116): cards, ⋯, the panel, the Edit / Add sheets
    if (this.tab === 'tenants') this.tenantsTab.bind(root);

    // contacts: add / delete / draft
    $('#mtr-cf-add')?.addEventListener('click', async () => {
      const name = $('#mtr-cf-name')?.value?.trim();
      if (!name) { alert('Contact name is required.'); return; }
      const j = await this.post('/contacts', {
        name,
        org: $('#mtr-cf-org')?.value || null,
        kind: $('#mtr-cf-kind')?.value || 'other',
        email: $('#mtr-cf-email')?.value || null,
        phone: $('#mtr-cf-phone')?.value || null,
        notes: $('#mtr-cf-notes')?.value || null,
      });
      if (j) { await this.reload('contacts'); this.draw(); }
    });
    root.querySelectorAll<HTMLElement>('[data-mtr-ct-del]').forEach(el =>
      el.addEventListener('click', async () => {
        if (!confirm('Delete this contact?')) return;
        if (await this.post('/contacts', { id: el.dataset.mtrCtDel, _delete: true })) { await this.reload('contacts'); this.draw(); }
      }));
    root.querySelectorAll<HTMLElement>('[data-mtr-draft]').forEach(el =>
      el.addEventListener('click', async () => {
        const j = await this.post('/outreach-draft', { contact_id: el.dataset.mtrDraft });
        if (j) this.showDraftModal(el.dataset.mtrDraft!, j);
      }));
  }

  // ── R114: pipeline board — drag & drop, moves, the card drawer ─────────────
  // HTML5 drag & drop between columns (the TasksPage pattern). Touch browsers
  // don't do HTML5 DnD reliably — ⋯ Move to and the drawer's stage select are
  // the complete touch path (all stages).
  private bindBoard(body: HTMLElement): void {
    const board = body.querySelector<HTMLElement>('.mtr-kanban');
    if (!board) return;
    const clearOver = () => board.querySelectorAll('.mtr-col.over').forEach(c => c.classList.remove('over'));
    board.querySelectorAll<HTMLElement>('.mtr-lead[data-mtr-card]').forEach(card => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('a, button, input, select, textarea, .mtr-check, .p, [data-kebab], [data-mtr-open]')) return;
        this.openDrawer(card.dataset.mtrCard!);
      });
      card.addEventListener('keydown', (e) => {
        if (e.target === card && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this.openDrawer(card.dataset.mtrCard!); }
      });
      card.addEventListener('dragstart', (e) => {
        closeKebab();
        this.dragLeadId = card.dataset.mtrCard!;
        card.classList.add('dragging');
        board.classList.add('dragging');
        try { e.dataTransfer?.setData('text/plain', this.dragLeadId); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; } catch { /* older engines */ }
      });
      card.addEventListener('dragend', () => {
        this.dragLeadId = null;
        card.classList.remove('dragging');
        board.classList.remove('dragging');
        clearOver();
      });
    });
    board.querySelectorAll<HTMLElement>('.mtr-col[data-mtr-col]').forEach(col => {
      col.addEventListener('dragover', (e) => {
        if (!this.dragLeadId) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (!col.classList.contains('over')) { clearOver(); col.classList.add('over'); }
      });
      col.addEventListener('dragleave', (e) => { if (!col.contains(e.relatedTarget as Node)) col.classList.remove('over'); });
      col.addEventListener('drop', (e) => {
        e.preventDefault();
        clearOver();
        const id = this.dragLeadId || (() => { try { return e.dataTransfer?.getData('text/plain') || ''; } catch { return ''; } })();
        this.dragLeadId = null;
        board.classList.remove('dragging');
        if (id) void this.moveLead(id, col.dataset.mtrCol!);
      });
    });
    // Edge auto-scroll: 11 columns ≈ 2.4k px — dragging New → Signed on a
    // laptop needs the board to scroll under the pointer. dragover fires
    // continuously while hovering, so each tick nudges the scroll.
    board.addEventListener('dragover', (e) => {
      if (!this.dragLeadId) return;
      const r = board.getBoundingClientRect();
      const EDGE = 60, STEP = 24;
      if (e.clientX < r.left + EDGE) board.scrollLeft -= STEP;
      else if (e.clientX > r.right - EDGE) board.scrollLeft += STEP;
    });
  }

  private needsConfirm(from: string, to: string): boolean {
    if (TERMINAL.includes(from)) return true;   // reopening a lost / ended card
    const order = this.stages();
    return SETTLED.includes(from) && !TERMINAL.includes(to) && order.indexOf(to) < order.indexOf(from);
  }

  // Optimistic: the card moves now; the POST carries expect_stage (the stage
  // this board showed) — a 409 means someone else moved it first: the board
  // reloads and says so. Any other failure puts the card back.
  private async moveLead(id: string, to: string, opts: { undo?: boolean; via?: string; skipTour?: boolean; confirmed?: boolean; from?: string } = {}): Promise<void> {
    const l = this.leadById(id);
    if (!l || !this.stages().includes(to) || l.stage === to || this.moving.has(id)) return;
    const from = String(l.stage);
    if (opts.from && opts.from !== from) return;   // the sheet's "Just move" for a card that moved meanwhile
    if (!opts.undo && !opts.confirmed && this.needsConfirm(from, to)
      && !confirm(`Move ${this.stageLabel(from)} → ${this.stageLabel(to)}?\n\nNothing is deleted — checks, payment months and notes stay on the card.`)) return;
    // R116 #7 (Kai: "Touring = a tour is scheduled"): a move INTO Touring with no
    // tour booked is caught BEFORE it is sent — the Schedule-tour sheet opens
    // with suggested days. "Schedule" = one write (stage + tour → its task);
    // "Just move" = this plain move (its Undo toast intact). The "→ next"
    // pass-through gets the same sheet with "Just move" focused.
    if (to === 'touring' && !opts.undo && !opts.skipTour && !this.tours.hasUpcoming(l)) {
      this.tours.openSheet({ mode: 'move', leadId: id, from, focusJustMove: opts.via === 'next' });
      return;
    }
    let note: string | undefined;
    if (!opts.undo && TERMINAL.includes(to)) {
      const why = prompt(`Moving to ${this.stageLabel(to)} — reason (optional, saved as a note on the card):`, '');
      if (why === null) return;
      note = why.trim() || undefined;
    }
    this.moving.add(id);
    l.stage = to;
    this.draw();
    this.patchDrawer();
    const r = await this.send('POST', `/leads/${encodeURIComponent(id)}`, { stage: to, expect_stage: from, ...(note ? { note } : {}) });
    this.moving.delete(id);
    // R114 fix (review mtr#1): a failed move (409 stale, 5xx, network) wrote
    // nothing — the typed Lost / Ended reason must not vanish with it. It is
    // handed back in the card's note composer (the user re-saves it once they
    // decide where the card goes — never auto-posted: after a 409 the lead
    // may now be Signed, where a "lost" reason would be wrong), else spelled
    // out in the alert to copy. Runs AFTER the board is fresh, so the lead
    // the composer opens on still exists. Never discards another draft / edit.
    const keepReason = (): string => {
      if (!note) return '';
      const safe = this.tab === 'leads' && !!this.leadById(id) && !this.editNote && !this.noteDraft.trim() && (!this.drawerLeadId || this.drawerLeadId === id);
      if (safe) {
        this.noteDraft = note;     // no lead switch here (drawer closed or on this lead), so openDrawer keeps it
        this.openDrawer(id, true); // paints the composer WITH the reason before the alert shows
        return '\n\nYour reason was not saved — it is in this card’s note box. Save it there if it still applies.';
      }
      return `\n\nYour reason was NOT saved — copy it if you still need it:\n“${note}”`;
    };
    if (r.ok && r.j && r.j.id) {
      this.replaceLead(r.j);
      if (!opts.undo) this.showToast(`Moved to <b>${this.esc(this.stageLabel(to))}</b>${note ? ' · reason saved as a note' : ''}`, () => { void this.moveLead(id, from, { undo: true }); });
    } else if (r.status === 409) {
      if (!(await this.reloadLeads())) {
        if (r.j.lead && r.j.lead.id) this.replaceLead(r.j.lead);
        else { const cur = this.leadById(id); if (cur) cur.stage = r.j.stage || from; }
      }
      this.draw();
      const kept = keepReason();
      alert((r.j.error || 'Someone else moved this lead — the board has been refreshed.') + kept);
    } else {
      const cur = this.leadById(id);
      if (cur) cur.stage = from;
      this.draw();
      const base = r.status === 0
        ? `Couldn’t reach the server — the lead is back in ${this.stageLabel(from)}.`
        : (r.j.error || `Couldn’t move the lead (${r.status}) — it is back in ${this.stageLabel(from)}.`);
      const kept = keepReason();
      alert(base + kept);
    }
    this.draw();
    this.patchDrawer();
  }

  private showToast(html: string, undo?: () => void): void {
    const host = this.container.querySelector<HTMLElement>('#mtr-toast-host');
    if (!host) return;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    host.innerHTML = `<div class="mtr-toast" role="status"><span>${html}</span>${undo ? '<button type="button" data-mtr-undo>Undo</button>' : ''}</div>`;
    host.querySelector('[data-mtr-undo]')?.addEventListener('click', () => {
      host.innerHTML = '';
      if (this.toastTimer) clearTimeout(this.toastTimer);
      undo?.();
    });
    this.toastTimer = setTimeout(() => { host.innerHTML = ''; }, 6000);
  }

  private async toggleCheck(id: string, key: string): Promise<void> {
    const lead = this.leadById(id);
    const cur = lead?.checks?.[key] || { done: false };
    let j: any = null;
    if (cur.done) {
      if (!confirm(`Clear the ${key} check?`)) return;
      j = await this.post(`/leads/${encodeURIComponent(id)}`, { check: { key, done: false } });
    } else {
      const note = prompt(`Mark ${key} check done — note (optional):`, cur.note || '');
      if (note === null) return;
      j = await this.post(`/leads/${encodeURIComponent(id)}`, { check: { key, done: true, note } });
    }
    if (j) await this.afterLeadWrite(j);
  }
  private async cyclePayment(id: string, month: string): Promise<void> {
    const lead = this.leadById(id);
    const cur = (lead?.payments || []).find((p: any) => p.month === month);
    const order = ['due', 'paid', 'late'];
    const next = order[(order.indexOf(cur?.status || 'due') + 1) % 3];
    const j = await this.post(`/leads/${encodeURIComponent(id)}`, { payment: { month, status: next } });
    if (j) await this.afterLeadWrite(j);
  }
  private async addPaymentMonth(id: string): Promise<void> {
    const month = prompt('Payment month (YYYY-MM):', new Date().toISOString().slice(0, 7));
    if (!month) return;
    const j = await this.post(`/leads/${encodeURIComponent(id)}`, { payment: { month, status: 'due' } });
    if (j) await this.afterLeadWrite(j);
  }
  private uploadDoc(id: string): void {
    let input = document.getElementById('mtr-doc-file') as HTMLInputElement | null;
    if (!input) {   // the drawer can outlive the board markup — bring a picker
      input = document.createElement('input');
      input.type = 'file'; input.id = 'mtr-doc-file'; input.style.display = 'none';
      this.container.appendChild(input);
    }
    const picker = input;
    picker.onchange = async () => {
      const file = picker.files?.[0];
      picker.value = '';
      if (!file) return;
      const check = prompt('Attach to which check? (background / credit / lease / deposit — empty for none):', 'background');
      if (check === null) return;
      const fd = new FormData();
      fd.append('file', file);
      if (check) fd.append('check', check.trim());
      const r = await fetch(`/api/mtr/leads/${encodeURIComponent(id)}/doc`, { method: 'POST', body: fd }).catch(() => null);
      const j = r ? await r.json().catch(() => ({} as any)) : { error: 'Network error — check your connection and try again.' };
      if (!r || !r.ok) { alert(j.error || 'upload failed'); return; }
      await this.afterLeadWrite(j.lead);
    };
    picker.click();
  }

  // ── the card drawer ───────────────────────────────────────────────────────
  private openDrawer(id: string, focusNote = false): void {
    if (!this.leadById(id)) return;
    if (this.drawerLeadId && this.drawerLeadId !== id) {
      if (this.noteDraft.trim() && !confirm('Discard the note you were writing?')) return;
      this.noteDraft = ''; this.editNote = null; this.histOpen = false;
    }
    this.drawerLeadId = id;
    this.focusComposer = focusNote;
    this.tours.resetForLead(id);
    closeKebab();
    // a board move's Undo toast would sit on top of the drawer — drop it
    const toast = this.container.querySelector<HTMLElement>('#mtr-toast-host');
    if (toast && toast.innerHTML) { toast.innerHTML = ''; if (this.toastTimer) clearTimeout(this.toastTimer); }
    void this.loadPeople();
    this.patchDrawer();
  }
  private async openDrawerById(id: string): Promise<void> {
    if (!this.leadById(id)) {
      await this.reloadLeads();
      if (this.tab === 'leads') this.draw();
    }
    if (this.tab === 'leads') this.openDrawer(id);
  }
  private requestCloseDrawer(): void {
    const editing = this.editNote && this.editNote.text.trim() !== this.editNote.orig.trim();
    if ((this.noteDraft.trim() || editing) && !confirm('Discard the note you were writing?')) return;
    this.closeDrawer();
  }
  private closeDrawer(): void {
    this.drawerLeadId = null;
    this.tours.resetForLead(null);
    this.noteDraft = '';
    this.editNote = null;
    this.histOpen = false;
    this.focusComposer = false;
    this.patchDrawer();
  }
  private attachEsc(): void {
    if (this.escOff) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const host = document.getElementById('mtr-drawer-host');
      if (!host || !host.isConnected || !this.drawerLeadId) { this.escOff?.(); return; }
      if (document.querySelector('[data-kebab-pop]')) return;   // the ⋯ menu closes first
      if (this.tours.isOpen()) return;                          // R116 #7: the tour sheet closes first
      e.preventDefault();
      this.requestCloseDrawer();
    };
    document.addEventListener('keydown', onKey);
    this.escOff = () => { document.removeEventListener('keydown', onKey); this.escOff = null; };
  }

  // Re-render ONLY the drawer host (the board is untouched); keeps the body's
  // scroll position and the focused textarea + caret across the repaint.
  private patchDrawer(): void {
    const host = this.container.querySelector<HTMLElement>('#mtr-drawer-host');
    if (!host) return;
    const l = this.tab === 'leads' ? this.leadById(this.drawerLeadId) : null;
    if (!l) {
      if (this.drawerLeadId && this.leadsData) { this.drawerLeadId = null; this.noteDraft = ''; this.editNote = null; }
      if (host.innerHTML) { closeKebab(); host.innerHTML = ''; }
      this.escOff?.();
      return;
    }
    closeKebab();
    const ae = document.activeElement as HTMLTextAreaElement | null;
    const fk = ae && host.contains(ae) ? ae.getAttribute('data-mtr-fk') : null;
    const selA = fk && typeof ae!.selectionStart === 'number' ? ae!.selectionStart : null;
    const selB = fk && typeof ae!.selectionEnd === 'number' ? ae!.selectionEnd : null;
    const prevBody = host.querySelector<HTMLElement>('.mtr-dr-body');
    const scroll = prevBody ? prevBody.scrollTop : 0;
    const fresh = !prevBody;
    host.innerHTML = this.renderDrawer(l, fresh);
    const bodyEl = host.querySelector<HTMLElement>('.mtr-dr-body');
    if (bodyEl && !fresh) bodyEl.scrollTop = scroll;
    const want = this.focusComposer ? 'compose' : this.editNote && !fk ? 'edit' : fk;
    if (want) {
      const el = host.querySelector<HTMLTextAreaElement>(`[data-mtr-fk="${want}"]`);
      if (el) {
        el.focus({ preventScroll: !this.focusComposer });
        if (want === fk && selA != null && selB != null) { try { el.setSelectionRange(selA, selB); } catch { /* not a text field */ } }
        else if (el.value) { try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* */ } }
        if (this.focusComposer) el.scrollIntoView({ block: 'nearest' });
      }
    }
    this.focusComposer = false;
    // per-note ⋯ items: bound per element (an open menu is moved to <body>)
    host.querySelectorAll<HTMLElement>('[data-mtr-note-edit]').forEach(el =>
      el.addEventListener('click', () => this.startEditNote(el.dataset.mtrNoteEdit!)));
    host.querySelectorAll<HTMLElement>('[data-mtr-note-del]').forEach(el =>
      el.addEventListener('click', () => { void this.deleteNoteById(el.dataset.mtrNoteDel!); }));
    this.tours.bindDrawer(host, l);   // R116 #7: the tour rows' ⋯ items
    this.attachEsc();
  }

  private histWhat(h: any): string {
    const cap = (s: string) => s ? s[0].toUpperCase() + s.slice(1) : '';
    switch (h?.kind) {
      case 'stage': return `${this.stageLabel(h.from)} → ${this.stageLabel(h.to)}`;
      case 'note': return 'Note added';
      case 'note_edit': return 'Note edited';
      case 'note_delete': return 'Note deleted';
      case 'created': return h.note === 'lead created' ? 'Lead created' : cap(String(h.note || 'Lead created'));
      default: return cap(String(h?.note || '')) || '—';
    }
  }

  private renderDrawer(l: any, fresh: boolean): string {
    const stages = this.stages();
    const idx = stages.indexOf(l.stage);
    const next = (idx >= 0 && idx < stages.indexOf('active')) ? stages[idx + 1] : null;
    const reopen = TERMINAL.includes(l.stage) ? String(l.reopen_to || 'contacted') : null;
    const busy = this.moving.has(l.id);
    const notes: any[] = (Array.isArray(l.notes) ? l.notes : []).slice().reverse();   // newest first
    const meta = `${this.listingName(l.property_interest)}${l.move_in ? ` · in ${this.fmtD(l.move_in)}` : ''}${l.term_months ? ` · ${l.term_months}mo` : ''}`;

    const noteHtml = (n: any) => {
      const anon = !n.by;
      const who = anon ? 'author not recorded' : this.whoName(n.by);
      const editing = !!(this.editNote && this.editNote.id === n.id);
      const edited = n.edited_at ? ` · edited${n.edited_by && String(n.edited_by).toLowerCase() !== String(n.by || '').toLowerCase() ? ` by ${this.esc(this.whoName(n.edited_by))}` : ''}` : '';
      const menu = this.canEditNote(n) && !editing
        ? kebabHtml([
          { icon: '✎', label: 'Edit note', attrs: { 'data-mtr-note-edit': n.id } },
          { icon: '🗑', label: 'Delete note', danger: true, attrs: { 'data-mtr-note-del': n.id } },
        ], { tip: 'Edit or delete this note' })
        : '';
      const body = editing
        ? `<textarea class="mtr-dr-ta" data-mtr-dr-edit-text data-mtr-fk="edit" maxlength="4000" aria-label="Edit note">${this.esc(this.editNote!.text)}</textarea>
           <div class="mtr-dr-row">
             <span class="mtr-dr-hint">⌘/Ctrl + Enter saves · Esc cancels</span>
             <button type="button" class="btn btn-ghost btn-sm" data-mtr-dr="edit-cancel">Cancel</button>
             <button type="button" class="btn btn-primary btn-sm" data-mtr-dr="edit-save" ${this.editNote!.busy ? 'disabled' : ''}>${this.editNote!.busy ? 'Saving…' : 'Save'}</button>
           </div>`
        : `<div class="mtr-dr-note-t">${this.esc(n.text)}</div>`;
      return `<div class="mtr-dr-note${editing ? ' editing' : ''}" data-note-id="${this.esc(n.id)}">
        <div class="mtr-dr-note-h"><span class="who${anon ? ' anon' : ''}">${this.esc(who)}</span><span class="when" title="${this.esc(this.fmtWhen(n.at))}">· ${this.esc(this.ago(n.at))}${edited}</span>${menu}</div>
        ${body}
      </div>`;
    };

    // details
    const m = l.move_in ? new Date(l.move_in + 'T00:00:00Z').getUTCMonth() + 1 : null;
    const eff = (l.property_interest && m) ? this.effRateOf(l.property_interest, m) : { rate: null, source: '' };
    const pct = (l.offer_price != null && eff.rate) ? Math.round(l.offer_price / (eff.rate as number) * 1000) / 10 : null;
    const offer = l.offer_price == null ? '—'
      : `${this.money(l.offer_price)}/mo${pct != null ? ` · <b>${pct}%</b> of ${this.money(eff.rate)} (${this.esc(eff.source)})` : ' · no rate to compare'}`;
    const thread = this.safeUrl(l.thread_url);
    const kv = (k: string, v: string) => `<div class="mtr-dr-kv"><span class="k">${k}</span><span class="v">${v}</span></div>`;
    const details = [
      kv('Property', l.property_interest ? this.tours.homeCellHtml(l) : this.esc(this.listingName(l.property_interest))),   // R116 #7: Change… asks about its tours
      kv('Move-in', `${this.esc(this.fmtFull(l.move_in))}${l.term_months ? ` · ${this.esc(l.term_months)} mo` : ''}`),
      kv('Offer', offer),
      kv('Contact', l.contact ? this.esc(l.contact) : '—'),
      (l.source || thread) ? kv('Source', `${this.esc(l.source || 'Lead inbox')}${thread ? ` · <a href="${this.esc(thread)}" target="_blank" rel="noopener noreferrer">Open conversation ↗</a>` : ''}`) : '',
      kv('Added', this.esc(this.fmtWhen(l.created_at) || '—')),
    ].join('');

    // checks — the note + doc that used to hide in a hover tooltip
    const checks = CHECKS.map(c => {
      const st = l.checks?.[c.key] || { done: false };
      const url = this.safeUrl(st.url);
      return `<div class="mtr-dr-check">
        <button type="button" class="mtr-check ${st.done ? 'done' : ''}" data-mtr-dr-check="${c.key}" title="${st.done ? 'Clear' : 'Mark done'} — ${c.label}" aria-label="${c.label}: ${st.done ? 'done — click to clear' : 'not done — click to mark done'}">${c.short}</button>
        <div class="mtr-dr-check-b">
          <div class="t">${c.label} <span class="st ${st.done ? 'ok' : ''}">${st.done ? 'Done' : 'Not done'}</span></div>
          ${st.note ? `<div class="n">${this.esc(st.note)}</div>` : ''}
          ${url ? `<a href="${this.esc(url)}" target="_blank" rel="noopener noreferrer">📎 View doc</a>` : ''}
        </div>
      </div>`;
    }).join('');

    const pays: any[] = l.payments || [];
    const showPays = l.stage === 'active' || pays.length > 0;
    const payRows = pays.map((p: any) => `<div class="mtr-dr-pay">
        <span class="mono">${this.esc(p.month)}</span>
        <button type="button" class="mtr-dr-paybtn ${this.esc(p.status)}" data-mtr-dr-pay="${this.esc(p.month)}" title="Click cycles due → paid → late">${this.esc(p.status)}</button>
        ${p.note ? `<span class="n">${this.esc(p.note)}</span>` : ''}
      </div>`).join('');

    const hist: any[] = (Array.isArray(l.history) ? l.history : []).slice().reverse();
    const stageOpts = stages.map(s => `<option value="${s}" ${s === l.stage ? 'selected' : ''}>${this.esc(this.stageLabel(s))}</option>`).join('');
    const canAdd = !!this.noteDraft.trim() && !this.noteBusy;

    return `
      <div class="mtr-dr-overlay${fresh ? ' enter' : ''}" data-mtr-dr-backdrop>
        <aside class="mtr-dr" role="dialog" aria-modal="true" aria-label="Pipeline lead">
          <header class="mtr-dr-head">
            <div class="mtr-dr-eyebrow">
              <span class="mtr-dr-tag">Pipeline lead</span>
              <span class="mtr-dr-meta">${this.esc(meta)}</span>
              <button type="button" class="mtr-dr-x" data-mtr-dr="close" aria-label="Close" title="Close (Esc)">✕</button>
            </div>
            <h3 class="mtr-dr-name">${this.esc(l.name)}</h3>
            <div class="mtr-dr-stage">
              <label>Stage <select data-mtr-stage-sel ${busy ? 'disabled' : ''} aria-label="Move this lead to another stage">${stageOpts}</select></label>
              ${next ? `<button type="button" class="btn btn-primary btn-sm" data-mtr-dr="move" data-to="${next}" ${busy ? 'disabled' : ''}>→ ${this.esc(this.stageLabel(next))}</button>` : ''}
              ${reopen ? `<button type="button" class="btn btn-secondary btn-sm" data-mtr-dr="move" data-to="${this.esc(reopen)}" ${busy ? 'disabled' : ''}>↺ Reopen to ${this.esc(this.stageLabel(reopen))}</button>` : ''}
              ${busy ? '<span class="mtr-dr-hint">Saving…</span>' : ''}
            </div>
          </header>
          <div class="mtr-dr-body">
            ${this.tours.sectionHtml(l)}
            <section class="mtr-dr-sec">
              <h4>Notes <span class="cnt">${notes.length}</span></h4>
              <div class="mtr-dr-compose">
                <textarea class="mtr-dr-ta" data-mtr-dr-compose data-mtr-fk="compose" maxlength="4000" placeholder="Add a note — what was said, what happens next…" aria-label="New note">${this.esc(this.noteDraft)}</textarea>
                <div class="mtr-dr-row">
                  <span class="mtr-dr-hint">⌘/Ctrl + Enter to add · everyone on the office team sees it</span>
                  <button type="button" class="btn btn-primary btn-sm" data-mtr-dr="add-note" ${canAdd ? '' : 'disabled'}>${this.noteBusy ? 'Saving…' : 'Add note'}</button>
                </div>
              </div>
              <div class="mtr-dr-notes">
                ${notes.length ? notes.map(noteHtml).join('') : '<div class="mtr-dr-empty">No notes yet — the first one shows on the card.</div>'}
              </div>
            </section>
            <section class="mtr-dr-sec">
              <h4>Details</h4>
              ${details}
            </section>
            <section class="mtr-dr-sec">
              <h4>Checks</h4>
              <div>${checks}</div>
              <div class="mtr-dr-row" style="margin-top:10px;"><button type="button" class="btn btn-secondary btn-sm" data-mtr-dr="doc">📎 Attach a doc</button></div>
            </section>
            ${showPays ? `<section class="mtr-dr-sec">
              <h4>Payments</h4>
              <div>${payRows || '<div class="mtr-dr-empty">No payment months yet.</div>'}</div>
              <div class="mtr-dr-row" style="margin-top:10px;"><button type="button" class="btn btn-secondary btn-sm" data-mtr-dr="pay-add">＋ Add a month</button></div>
            </section>` : ''}
            <section class="mtr-dr-sec">
              <details class="mtr-dr-hist" data-mtr-dr-hist ${this.histOpen ? 'open' : ''}>
                <summary>History <span class="cnt">${hist.length}</span></summary>
                ${hist.length ? `<ul>${hist.map(h => `<li><span class="what">${this.esc(this.histWhat(h))}</span><span class="meta">${this.esc(h.by ? this.whoName(h.by) : '—')}${h.at ? ` · ${this.esc(this.fmtWhen(h.at))}` : ''}</span></li>`).join('')}</ul>` : '<div class="mtr-dr-empty">Nothing recorded yet.</div>'}
              </details>
            </section>
          </div>
        </aside>
      </div>`;
  }

  // ONE set of delegated listeners on the host (bound once per render()).
  private bindDrawerHost(host: HTMLElement): void {
    host.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.matches('[data-mtr-dr-backdrop]')) { this.requestCloseDrawer(); return; }
      const id = this.drawerLeadId;
      if (!id) return;
      if (this.tours.handleDrawerClick(t, id)) return;   // R116 #7: tour rows, Schedule tour, home change
      const chk = t.closest<HTMLElement>('[data-mtr-dr-check]');
      if (chk) { void this.toggleCheck(id, chk.dataset.mtrDrCheck!); return; }
      const pay = t.closest<HTMLElement>('[data-mtr-dr-pay]');
      if (pay) { void this.cyclePayment(id, pay.dataset.mtrDrPay!); return; }
      const act = t.closest<HTMLElement>('[data-mtr-dr]');
      if (!act || (act as HTMLButtonElement).disabled) return;
      switch (act.dataset.mtrDr) {
        case 'close': this.requestCloseDrawer(); break;
        case 'move': if (act.dataset.to) void this.moveLead(id, act.dataset.to, { via: 'next' }); break;
        case 'add-note': void this.submitNote(); break;
        case 'edit-save': void this.saveEditNote(); break;
        case 'edit-cancel': this.editNote = null; this.patchDrawer(); break;
        case 'doc': this.uploadDoc(id); break;
        case 'pay-add': void this.addPaymentMonth(id); break;
      }
    });
    host.addEventListener('input', (e) => {
      const t = e.target as HTMLTextAreaElement;
      if (this.tours.handleDrawerInput(t)) return;   // R116 #7: the home picker
      if (t.matches('[data-mtr-dr-compose]')) {
        this.noteDraft = t.value;
        const btn = host.querySelector<HTMLButtonElement>('[data-mtr-dr="add-note"]');
        if (btn) btn.disabled = !t.value.trim() || this.noteBusy;
      } else if (t.matches('[data-mtr-dr-edit-text]') && this.editNote) {
        this.editNote.text = t.value;
      }
    });
    host.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement;
      const submit = e.key === 'Enter' && (e.metaKey || e.ctrlKey);
      if (t.matches('[data-mtr-dr-compose]') && submit) { e.preventDefault(); void this.submitNote(); }
      else if (t.matches('[data-mtr-dr-edit-text]')) {
        if (submit) { e.preventDefault(); void this.saveEditNote(); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.editNote = null; this.patchDrawer(); }
      }
    });
    host.addEventListener('change', (e) => {
      const t = e.target as HTMLSelectElement;
      if (this.tours.handleDrawerInput(t)) return;   // R116 #7: the home picker / tours radios
      if (!t.matches('[data-mtr-stage-sel]') || !this.drawerLeadId) return;
      const id = this.drawerLeadId;
      void this.moveLead(id, t.value).then(() => this.patchDrawer());   // a cancelled confirm snaps the select back
    });
    // <details> toggle doesn't bubble — capture it
    host.addEventListener('toggle', (e) => {
      const t = e.target as HTMLDetailsElement;
      if (t && t.matches && t.matches('[data-mtr-dr-hist]')) this.histOpen = t.open;
    }, true);
  }

  private async submitNote(): Promise<void> {
    const id = this.drawerLeadId;
    const text = this.noteDraft.trim();
    if (!id || !text || this.noteBusy) return;
    this.noteBusy = true;
    this.patchDrawer();
    const r = await this.send('POST', `/leads/${encodeURIComponent(id)}`, { note: text });
    this.noteBusy = false;
    if (r.ok && r.j && r.j.id) {
      if (this.drawerLeadId === id && this.noteDraft.trim() === text) this.noteDraft = '';
      this.replaceLead(r.j);
      this.draw();
    } else {
      alert(r.j.error || 'Couldn’t save the note — it is still in the box, try again.');
    }
    this.patchDrawer();
  }
  private startEditNote(noteId: string): void {
    const n = (this.leadById(this.drawerLeadId)?.notes || []).find((x: any) => x.id === noteId);
    if (!n) return;
    this.editNote = { id: noteId, text: String(n.text || ''), orig: String(n.text || '') };
    // after the ⋯ menu has closed (Kebab closes on the next tick)
    setTimeout(() => this.patchDrawer(), 0);
  }
  private async saveEditNote(): Promise<void> {
    const id = this.drawerLeadId, ed = this.editNote;
    if (!id || !ed || ed.busy) return;
    if (!ed.text.trim()) { alert('A note can’t be empty — use ⋯ → Delete note to remove it.'); return; }
    ed.busy = true;
    this.patchDrawer();
    const r = await this.send('PUT', `/leads/${encodeURIComponent(id)}/notes/${encodeURIComponent(ed.id)}`, { text: ed.text });
    ed.busy = false;
    if (r.ok && r.j && r.j.lead) {
      if (this.editNote === ed) this.editNote = null;
      this.replaceLead(r.j.lead);
      this.draw();
    } else {
      alert(r.j.error || `Couldn’t save the edit (${r.status || 'network'}).`);
    }
    this.patchDrawer();
  }
  private async deleteNoteById(noteId: string): Promise<void> {
    const id = this.drawerLeadId;
    if (!id) return;
    await new Promise(r => setTimeout(r, 0));   // let the ⋯ menu close first
    if (!confirm('Delete this note? It can’t be undone — the history keeps only “note deleted”, who and when.')) return;
    const j = await this.del(`/leads/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`);
    if (j && j.lead) {
      if (this.editNote && this.editNote.id === noteId) this.editNote = null;
      this.replaceLead(j.lead);
      this.draw();
      this.patchDrawer();
    }
  }
}
